import { SupportedChain, SOLANA_PROGRAM_ID } from '../../constants';
import { getChainConfig } from '../../config/chains';
import {
  ChainAdapter,
  TransactionResult,
  WalletInfo,
  TicketInfo,
  GroupInfo,
  JackpotInfo,
  WhitelistInfo,
  GroupIdentifier,
} from '../interfaces/ChainAdapter';
import {
  Connection,
  PublicKey,
  LAMPORTS_PER_SOL,
  Transaction,
  TransactionInstruction,
  SendTransactionError,
  SystemProgram,
} from '@solana/web3.js';
import { createSolanaToken } from '../../utils/createSolanaToken';
import { solanaProgramClient } from '../solana/program';
import { solanaPDA } from '../solana/pda';
import { createSolanaSPLToken, SPLTokenInfo } from '../solana/spl-token';
import {
  calculateDistribution,
  ensureATAs,
} from '../../utils/solana-extra-token';

// Helper for development-only logging
const devLog = (...args: any[]) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('[SolanaAdapter]', ...args);
  }
};

export class SolanaAdapter implements ChainAdapter {
  readonly chain: SupportedChain = 'solana';
  readonly config = getChainConfig('solana');
  private listeners: Map<string, ((...args: any[]) => void)[]> = new Map();
  private wallet: any | null = null;
  private connection: Connection;

  constructor() {
    this.connection = new Connection(
      this.config.rpcUrl || 'https://api.devnet.solana.com',
      'confirmed',
    );
    this.initializeWallet();
  }

  /**
   * Validate Solana address (base58 format)
   * @param address - Address string to validate
   * @returns true if address is valid Solana address, false otherwise
   */
  validateAddress(address: string): boolean {
    if (!address || address.trim().length === 0) {
      return false;
    }

    const trimmedAddr = address.trim();

    // Solana addresses should not start with 0x (Ethereum format)
    if (trimmedAddr.startsWith('0x')) {
      return false;
    }

    try {
      const publicKey = new PublicKey(trimmedAddr);
      // PublicKey constructor will throw error if address is invalid
      // Verify the address matches the input (handles case sensitivity)
      return publicKey.toBase58() === trimmedAddr;
    } catch (error) {
      return false;
    }
  }

  /**
   * Đảm bảo dữ liệu nhận diện group cho buy_tickets_auto đầy đủ.
   * - blockhashHash & buyer là bắt buộc cho tất cả flow Solana hiện tại
   * - groupId vẫn được sử dụng làm tham số instruction (u64) -> yêu cầu ở hầu hết hàm
   */
  private ensureAutoGroupIdentifier(
    group: GroupIdentifier | undefined,
    options: {
      requireGroupId?: boolean;
      requireBlockhash?: boolean;
      requireBuyer?: boolean;
    } = { requireGroupId: true, requireBlockhash: true, requireBuyer: true },
  ): {
    blockhashHash?: string;
    buyerPublicKey?: PublicKey;
    buyerAddress?: string;
    groupId?: string;
  } {
    if (!group) {
      throw new Error(
        'Thiếu thông tin group. Vui lòng truyền blockhashHash và buyer (buy_tickets_auto).',
      );
    }

    const { blockhashHash, buyer, groupId } = group;

    const requireGroupId = options.requireGroupId ?? true;
    const requireBlockhash = options.requireBlockhash ?? true;
    const requireBuyer = options.requireBuyer ?? true;

    if (requireBlockhash && (!blockhashHash || blockhashHash === '')) {
      throw new Error(
        'Thiếu blockhashHash cho group buy_tickets_auto. Vui lòng kiểm tra dữ liệu chiến dịch.',
      );
    }

    if (requireBuyer && (!buyer || buyer === '')) {
      throw new Error(
        'Thiếu buyer (creator wallet) cho group buy_tickets_auto. Vui lòng kiểm tra dữ liệu chiến dịch.',
      );
    }

    if (requireGroupId && (groupId === undefined || groupId === null)) {
      throw new Error(
        'Thiếu groupId (u64) cho instruction. Vui lòng đồng bộ dữ liệu group giữa BE và FE.',
      );
    }

    let buyerPublicKey: PublicKey | undefined;
    if (buyer) {
      try {
        buyerPublicKey = new PublicKey(buyer);
      } catch (error) {
        throw new Error(
          `buyer không hợp lệ: ${(error as Error)?.message || error}`,
        );
      }
    }

    return {
      blockhashHash,
      buyerPublicKey,
      buyerAddress: buyer,
      groupId,
    };
  }

  private async getProgramAdminPubkey(): Promise<PublicKey> {
    const globalState = await solanaProgramClient.getGlobalState();
    if (!globalState || !globalState.admin) {
      throw new Error(
        'Không thể tải global state của chương trình Solana để xác định ví admin.',
      );
    }

    return globalState.admin instanceof PublicKey
      ? globalState.admin
      : new PublicKey(globalState.admin);
  }

  private async getGroupOwnerPublicKey(params: {
    buyerPublicKey?: PublicKey;
    blockhashHash?: string | number | bigint;
  }): Promise<PublicKey | null> {
    if (!params.buyerPublicKey || !params.blockhashHash) {
      return null;
    }

    try {
      const groupState =
        await solanaProgramClient.getGroupStateByBuyerAndBlockhash(
          params.buyerPublicKey,
          params.blockhashHash,
        );

      const ownerField = groupState?.owner;
      if (!ownerField) {
        return null;
      }

      return ownerField instanceof PublicKey
        ? ownerField
        : new PublicKey(ownerField.toString());
    } catch (error) {
      console.warn(
        '[SolanaAdapter] Không thể tải group owner:',
        error instanceof Error ? error.message : error,
      );
      return null;
    }
  }

  private async assertWhitelistAuthority(
    walletKey: PublicKey,
    groupOwner?: PublicKey | null,
  ): Promise<void> {
    const adminPk = await this.getProgramAdminPubkey();
    if (walletKey.equals(adminPk)) {
      return;
    }
    if (groupOwner && walletKey.equals(groupOwner)) {
      return;
    }
    const adminStr = adminPk.toBase58();
    const ownerStr = groupOwner?.toBase58();
    const ownerMessage = ownerStr
      ? ` hoặc owner group ${ownerStr}`
      : '';
    throw new Error(
      `Ví hiện tại (${walletKey.toBase58()}) không có quyền thực hiện thao tác này trên Solana. ` +
        `Vui lòng đăng nhập bằng ví admin ${adminStr}${ownerMessage}.`,
    );
  }

  private initializeWallet() {
    if (typeof window !== 'undefined' && (window as any).solana) {
      const phantom = (window as any).solana;

      if (phantom.isPhantom) {
        this.wallet = phantom;

        // Disable auto-connect to prevent automatic connection
        if (phantom.autoConnect) {
          phantom.autoConnect = false;
        }

        // Set up event listeners
        this.setupWalletEventListeners();
      }
    }
  }

  private setupWalletEventListeners() {
    if (!this.wallet) return;

    this.wallet.on('connect', async (publicKey: PublicKey) => {
      const balance = await this.getWalletBalance();
      this.emit('walletChanged', {
        address: publicKey.toString(),
        balance: balance.toString(),
        symbol: this.config.symbol,
        isConnected: true,
        chain: this.chain,
      });
    });

    this.wallet.on('disconnect', () => {
      this.emit('walletChanged', null);
    });
  }

  // Helper method to ensure wallet is available
  private ensureWallet(): any | null {
    if (!this.wallet) {
      // Try to re-detect wallet
      if (typeof window !== 'undefined' && (window as any).solana) {
        const phantom = (window as any).solana;
        if (phantom.isPhantom) {
          this.wallet = phantom;
          this.setupWalletEventListeners();
        }
      }
    }
    return this.wallet;
  }

  // Helper method to prepare transaction with recent blockhash
  // This ensures Phantom wallet can display the confirmation popup
  private async prepareTransaction(
    transaction: Transaction,
  ): Promise<Transaction> {
    const { blockhash } = await this.connection.getLatestBlockhash('confirmed');
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = this.wallet?.publicKey;
    return transaction;
  }

  /**
   * Simulate transaction to surface detailed logs before sending.
   * Useful for debugging unexpected RPC failures (only runs outside production).
   */
  private async debugSimulateTransaction(
    transaction: Transaction,
    label: string,
  ): Promise<void> {
    if (process.env.NODE_ENV === 'production') {
      return;
    }

    try {
      const simulation = await this.connection.simulateTransaction(transaction);

      if (simulation.value.err) {
        console.warn(
          `[SolanaAdapter][${label}] Simulation error:`,
          simulation.value.err,
        );
      }

      if (simulation.value.logs?.length) {
        console.warn(
          `[SolanaAdapter][${label}] Simulation logs:\n`,
          simulation.value.logs.join('\n'),
        );
      }
    } catch (simulationError) {
      console.warn(
        `[SolanaAdapter][${label}] Simulation failed:`,
        simulationError,
      );
    }
  }

  // Wallet management
  async connectWallet(): Promise<WalletInfo> {
    try {
      devLog('SolanaAdapter: Starting connectWallet process');

      const wallet = this.ensureWallet();
      if (!wallet) {
        throw new Error(
          'Phantom wallet not found. Please install Phantom wallet.',
        );
      }

      devLog('SolanaAdapter: Phantom wallet found, checking connection status');
      devLog('SolanaAdapter: Wallet isConnected:', wallet.connected);

      // Check if already connected (handle undefined connected state)
      const isConnected = wallet.connected === true;
      if (isConnected && wallet.publicKey) {
        devLog('SolanaAdapter: Wallet already connected, getting balance');
        const balance = await this.getWalletBalance();

        const walletInfo: WalletInfo = {
          address: wallet.publicKey.toString(),
          balance: balance.toString(),
          symbol: this.config.symbol,
          isConnected: true,
          chain: this.chain,
        };

        devLog('SolanaAdapter: Returning existing wallet info:', walletInfo);
        this.emit('walletChanged', walletInfo);
        return walletInfo;
      }

      // Connect to wallet with proper error handling
      try {
        // Ensure we're connecting to Solana, not Ethereum
        devLog(
          'SolanaAdapter: Connecting to Solana network via Phantom wallet...',
        );
        await wallet.connect();
        devLog('SolanaAdapter: Wallet connection successful');

        if (!wallet.publicKey) {
          throw new Error('Failed to get wallet public key after connection');
        }

        devLog('SolanaAdapter: Getting wallet balance');
        const balance = await this.getWalletBalance();
        const walletInfo: WalletInfo = {
          address: wallet.publicKey.toString(),
          balance: balance.toString(),
          symbol: this.config.symbol,
          isConnected: true,
          chain: this.chain,
        };

        // Emit wallet change event
        this.emit('walletChanged', walletInfo);
        devLog('SolanaAdapter: Wallet connected successfully:', walletInfo);

        return walletInfo;
      } catch (connectError: any) {
        console.error('Phantom wallet connect error:', connectError);

        // Handle specific Phantom wallet errors
        if (connectError.code === 4001) {
          throw new Error('User rejected the connection request');
        } else if (connectError.code === 4100) {
          throw new Error('Unauthorized: Please unlock your Phantom wallet');
        } else if (connectError.code === 4200) {
          throw new Error(
            'Unsupported account: Please check your Phantom wallet account',
          );
        } else if (connectError.code === 4900) {
          throw new Error(
            'Wallet not connected: Please connect your Phantom wallet',
          );
        } else if (
          connectError.message &&
          connectError.message.includes('unsupported account')
        ) {
          throw new Error(
            'Unsupported account: Please check your Phantom wallet account or try switching networks',
          );
        } else if (
          connectError.message &&
          connectError.message.includes('Ethereum')
        ) {
          throw new Error(
            'This website is trying to use Ethereum, but your Phantom wallet is configured for Solana. Please switch to a Solana-compatible account or use a different wallet.',
          );
        } else {
          throw new Error(
            `Phantom wallet connection failed: ${
              connectError.message || 'Unknown error'
            }`,
          );
        }
      }
    } catch (error) {
      console.error('Wallet connection error:', error);
      throw new Error(
        `Failed to connect Solana wallet: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  async disconnectWallet(): Promise<void> {
    try {
      const wallet = this.ensureWallet();
      if (!wallet) {
        throw new Error('Wallet not available');
      }

      devLog('Disconnecting Phantom wallet...');
      await wallet.disconnect();
      this.emit('walletChanged', null);
      devLog('Wallet disconnected successfully');
    } catch (error) {
      console.error('Wallet disconnection error:', error);
      throw new Error(
        `Failed to disconnect Solana wallet: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  async getWalletBalance(): Promise<number> {
    try {
      const wallet = this.ensureWallet();
      if (!wallet || !wallet.connected || !wallet.publicKey) {
        return 0;
      }

      const balance = await this.connection.getBalance(wallet.publicKey);
      return balance / LAMPORTS_PER_SOL; // Convert lamports to SOL
    } catch (error) {
      console.error('Get wallet balance error:', error);
      return 0;
    }
  }

  async getWalletInfo(): Promise<WalletInfo | null> {
    try {
      const wallet = this.ensureWallet();
      if (!wallet || wallet.connected !== true) {
        return null;
      }

      const publicKey = wallet.publicKey;
      if (!publicKey) {
        return null;
      }

      const balance = await this.getWalletBalance();
      return {
        address: publicKey.toString(),
        balance: balance.toString(),
        symbol: this.config.symbol,
        isConnected: true,
        chain: this.chain,
      };
    } catch (error) {
      console.error('Get wallet info error:', error);
      return null;
    }
  }

  async isWalletConnected(): Promise<boolean> {
    try {
      const wallet = this.ensureWallet();
      if (!wallet) return false;

      // Simply return connection status without chain checking
      // Chain switching logic should be handled by ChainManager
      return wallet.connected === true;
    } catch (error) {
      console.error('Check wallet connection error:', error);
      return false;
    }
  }

  async signMessage(): Promise<string> {
    try {
      devLog('SolanaAdapter: Starting signMessage process');

      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Phantom wallet not connected');
      }

      devLog(
        'SolanaAdapter: Wallet found, creating Solana token with blockchain header',
      );

      // Sử dụng utility function riêng cho Solana với blockchain header
      const signedToken = await createSolanaToken(wallet, '30d');

      devLog(
        'SolanaAdapter: Solana token created successfully, token length:',
        signedToken.length,
      );
      return signedToken;
    } catch (error) {
      devLog('SolanaAdapter: Error creating Solana token:', error);
      console.error('SolanaAdapter: Full error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        error,
      });
      throw new Error(
        `Failed to create Solana token: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  // Core transactions - Real implementation with SPL token support (BNB parity)
  async buyTickets(params: {
    group: GroupIdentifier;
    quantity: number;
    price: string;
    extraTokenMint?: string;
    extraTokenAmount?: number;
    receivers?: string[];
  }): Promise<TransactionResult> {
    try {
      devLog('Solana buyTickets called with params:', params);

      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }
      const groupParams = this.ensureAutoGroupIdentifier(params.group, {
        requireGroupId: true,
        requireBlockhash: true,
        requireBuyer: true,
      });
      if (!groupParams.blockhashHash || !groupParams.buyerPublicKey) {
        throw new Error(
          'Thiếu blockhashHash hoặc buyerPublicKey để tạo group options',
        );
      }
      const groupOptions = {
        blockhashHash: groupParams.blockhashHash,
        buyer: groupParams.buyerPublicKey,
      };

      // Parse string groupId to number or bigint first
      const { parseGroupIdFromString, groupIdToNumber, groupIdToBN } =
        await import('../solana/pda');
      let groupId: number;
      try {
        const parsed = parseGroupIdFromString(groupParams.groupId as string);
        groupId = groupIdToNumber(parsed);
      } catch (error: any) {
        // If group_id is too large, use BigInt and convert to number (may lose precision)
        const parsed = parseGroupIdFromString(groupParams.groupId as string);
        const groupIdBN = groupIdToBN(parsed);
        groupId = Number(groupIdBN.toString());
        console.warn(
          '[buyTickets] group_id exceeds safe integer range, using BigInt:',
          groupParams.groupId,
        );
      }

      // Ensure receivers array exists
      // NOTE: Receivers can be empty array - receivers can be added later via whitelist
      // According to test case, receivers is optional and can be empty
      const receiversArray = params.receivers || [];

      // Validate and parse receivers (if provided)
      const receivers: PublicKey[] = [];
      const seenReceivers = new Set<string>();

      for (const addr of receiversArray) {
        try {
          if (!addr || typeof addr !== 'string') {
            throw new Error(`Invalid receiver address: ${addr}`);
          }

          // Validate not zero address
          if (
            addr === '11111111111111111111111111111111' ||
            addr === '0x0000000000000000000000000000000000000000'
          ) {
            throw new Error(`Receiver cannot be zero address: ${addr}`);
          }

          // Check for duplicates
          if (seenReceivers.has(addr.toLowerCase())) {
            console.warn('[buyTickets] Duplicate receiver address:', addr);
            // Allow duplicates but warn
          }
          seenReceivers.add(addr.toLowerCase());

          const pubkey = new PublicKey(addr);
          receivers.push(pubkey);
        } catch (error) {
          console.error(
            '[buyTickets] Failed to parse receiver address:',
            addr,
            error,
          );
          throw new Error(
            `Invalid receiver address: ${addr} - ${
              error instanceof Error ? error.message : 'Unknown error'
            }`,
          );
        }
      }

      console.log(
        `[buyTickets] Parsed ${receivers.length} receivers for ${params.quantity} tickets`,
      );

      // Preflight 1: Verify group state exists (buy_tickets_auto PDA)
      if (!groupParams.buyerPublicKey || !groupParams.blockhashHash) {
        throw new Error(
          'Thiếu buyerPublicKey hoặc blockhashHash để verify group state',
        );
      }
      try {
        const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
          groupParams.buyerPublicKey,
          groupParams.blockhashHash,
        );
        const acc = await this.connection.getAccountInfo(groupStatePDA);
        if (!acc) {
          throw new Error(
            `Group state (blockhashHash=${groupParams.blockhashHash}, buyer=${groupParams.buyerAddress}) chưa được khởi tạo. Vui lòng khởi tạo group trước khi mua.`,
          );
        }
      } catch (e) {
        throw e instanceof Error ? e : new Error(String(e));
      }

      // Preflight 2: Check admin signer requirement
      try {
        const globalState = await solanaProgramClient.getGlobalState();
        const adminFromState =
          globalState?.admin?.toString?.() ||
          globalState?.adminPubkey?.toString?.();
        if (
          adminFromState &&
          adminFromState !== this.ensureWallet()?.publicKey?.toString()
        ) {
          console.warn(
            '[buyTickets] Wallet is not admin. Program may require admin signer for buy_tickets:',
            { adminFromState },
          );
          // Cảnh báo sớm để tránh -32603 khó hiểu từ Phantom
          // Cho phép tiếp tục nếu chương trình chấp nhận buyer làm admin, ngược lại transaction sẽ fail có log rõ hơn
        }
      } catch (e) {
        console.debug(
          '[buyTickets] Could not fetch global state for admin check:',
          e,
        );
      }

      // Handle extra token if provided (BNB parity)
      if (
        params.extraTokenMint &&
        params.extraTokenAmount &&
        params.extraTokenAmount > 0
      ) {
        let extraTokenMint: PublicKey;
        try {
          extraTokenMint = new PublicKey(params.extraTokenMint);
        } catch (error) {
          throw new Error(
            `Invalid extra token mint address: ${params.extraTokenMint}`,
          );
        }

        // Step 0: Check token balance before proceeding
        // Note: getSPLTokenBalance returns UI amount (with decimals), but params.extraTokenAmount is raw
        // So we need to convert requiredAmount to UI amount for comparison
        // Note: Token approval is not required here because:
        // - buyTickets instruction doesn't transfer tokens (only records metadata)
        // - Transfers to receivers/vault are done directly from buyer's ATA (owner is signer)
        const tokenDecimals = await this.getTokenDecimals(
          params.extraTokenMint,
        );
        const buyerBalanceUI = await this.getSPLTokenBalance(
          params.extraTokenMint,
          wallet.publicKey.toString(),
        );

        // params.extraTokenAmount is already in raw format (from parseUnits in EnvelopeForm)
        // Convert to UI amount for comparison
        const requiredAmountUI =
          params.extraTokenAmount / Math.pow(10, tokenDecimals);

        if (buyerBalanceUI < requiredAmountUI) {
          throw new Error(
            `Insufficient token balance. Required: ${requiredAmountUI}, Available: ${buyerBalanceUI}`,
          );
        }

        // Step 1: Calculate distribution (BNB parity)
        // params.extraTokenAmount is already in smallest unit (raw amount with decimals)
        // So we don't multiply by this.config.decimals (which is for SOL, not SPL token)
        const distribution = calculateDistribution(
          params.extraTokenAmount, // Already raw amount from parseUnits
          params.quantity,
          2000, // 20% local jackpot
        );

        devLog('📊 Distribution calculated (BNB parity):', distribution);

        // Step 2: Ensure all ATAs exist
        await ensureATAs(this.connection, wallet, receivers, extraTokenMint);

        // Step 3: Get buyer ATA
        const { getAssociatedTokenAddress } = await import('@solana/spl-token');

        // Validate wallet.publicKey
        if (!wallet.publicKey) {
          throw new Error('Wallet public key is not available');
        }

        let buyerATA: PublicKey;
        try {
          buyerATA = await getAssociatedTokenAddress(
            extraTokenMint,
            wallet.publicKey,
          );
          console.log('[buyTickets] Buyer ATA:', buyerATA.toString());
        } catch (error) {
          console.error('[buyTickets] Failed to get buyer ATA:', error);
          throw new Error(
            `Failed to get buyer associated token address: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`,
          );
        }

        // Step 4: Get vault ATA
        // Use solanaPDA to ensure consistent derivation
        let vaultAuthority: PublicKey;
        let vaultATA: PublicKey;
        try {
          // ✅ Use solanaPDA.getExtraTokenVaultPDA to get vault authority
          // This matches the program's PDA derivation for buy_tickets_auto flow
          // Seeds: ["vault", buyer, blockhash_hash, mint]
          if (!groupParams.buyerPublicKey || !groupParams.blockhashHash) {
            throw new Error(
              'Thiếu buyerPublicKey hoặc blockhashHash để tạo vault authority',
            );
          }
          [vaultAuthority] = solanaPDA.getExtraTokenVaultPDA(
            groupParams.buyerPublicKey,
            groupParams.blockhashHash,
            extraTokenMint,
          );

          console.log(
            '[buyTickets] Vault authority:',
            vaultAuthority.toString(),
          );

          // Check if vault authority is on-curve (Static method exists in @solana/web3.js)
          let isOnCurveCheck = 'unknown';
          try {
            if (typeof PublicKey.isOnCurve === 'function') {
              isOnCurveCheck = PublicKey.isOnCurve(vaultAuthority.toBytes())
                ? 'yes'
                : 'no';
            }
          } catch (e) {
            isOnCurveCheck = 'error checking';
          }
          console.log(
            '[buyTickets] Vault authority is on curve:',
            isOnCurveCheck,
          );

          if (!vaultAuthority) {
            throw new Error('Vault authority is null or undefined');
          }

          // For off-curve PDAs, we cannot use getAssociatedTokenAddress directly
          // Instead, we need to manually derive the ATA using the same logic
          // ATA address = PDA(owner, token_program_id, mint)
          // But since vaultAuthority might be off-curve, we need to check if we can get ATA
          try {
            vaultATA = await getAssociatedTokenAddress(
              extraTokenMint,
              vaultAuthority,
            );
            console.log('[buyTickets] Vault ATA:', vaultATA.toString());
          } catch (ataError: any) {
            // If vault authority is off-curve, we need to derive ATA differently
            if (
              ataError?.message?.includes('OffCurve') ||
              ataError?.name === 'TokenOwnerOffCurveError'
            ) {
              console.warn(
                '[buyTickets] Vault authority is off-curve, using manual ATA derivation',
              );
              // Manually derive ATA for off-curve PDA
              // This should match the program's ATA derivation
              const { TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID } =
                await import('@solana/spl-token');
              const [ata] = PublicKey.findProgramAddressSync(
                [
                  vaultAuthority.toBuffer(),
                  TOKEN_PROGRAM_ID.toBuffer(),
                  extraTokenMint.toBuffer(),
                ],
                ASSOCIATED_TOKEN_PROGRAM_ID,
              );
              vaultATA = ata;
              console.log(
                '[buyTickets] Vault ATA (manual derivation):',
                vaultATA.toString(),
              );
            } else {
              throw ataError;
            }
          }
        } catch (error) {
          console.error('[buyTickets] Failed to get vault ATA:', error);
          throw new Error(
            `Failed to get vault associated token address: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`,
          );
        }

        // Step 5: Get receiver ATAs
        let receiverATAs: PublicKey[] = [];
        if (receivers.length > 0) {
          try {
            receiverATAs = await Promise.all(
              receivers.map(async (r, index) => {
                try {
                  if (!r) {
                    throw new Error(
                      `Receiver at index ${index} is null or undefined`,
                    );
                  }
                  // getAssociatedTokenAddress will throw TokenOwnerOffCurveError if receiver is off-curve
                  return await getAssociatedTokenAddress(extraTokenMint, r);
                } catch (error) {
                  console.error(
                    `[buyTickets] Failed to get ATA for receiver ${index}:`,
                    r.toString(),
                    error,
                  );
                  if (
                    error instanceof Error &&
                    error.message.includes('OffCurve')
                  ) {
                    throw new Error(
                      `Receiver at index ${index} (${r.toString()}) is not a valid on-curve address`,
                    );
                  }
                  throw new Error(
                    `Failed to get receiver ATA at index ${index}: ${
                      error instanceof Error ? error.message : 'Unknown error'
                    }`,
                  );
                }
              }),
            );
            console.log(
              '[buyTickets] Receiver ATAs:',
              receiverATAs.map(ata => ata.toString()),
            );
          } catch (error) {
            console.error('[buyTickets] Failed to get receiver ATAs:', error);
            throw error;
          }
        }

        // Step 6: Buy tickets
        // params.extraTokenAmount is already in raw format (smallest unit)
        console.log('[buyTickets] Creating buy tickets instruction...', {
          groupId,
          numTickets: params.quantity,
          extraTokenMint: extraTokenMint?.toString(),
          extraTokenAmount: params.extraTokenAmount,
          hasBuyerATA: !!buyerATA,
          hasVaultATA: !!vaultATA,
          receiversCount: receivers.length,
          receiverATAsCount: receiverATAs.length,
        });

        const instruction =
          await solanaProgramClient.createBuyTicketsInstruction(
            wallet,
            {
              groupId,
              numTickets: params.quantity,
              extraTokenMint,
              extraTokenAmount: params.extraTokenAmount, // Already raw amount
              receivers, // Receivers for NFT tickets (not for token transfers)
              // Pass extra token accounts for buy_tickets instruction
              // NOTE: receiverATAs KHÔNG được pass ở đây vì buy_tickets chỉ tạo tickets
              // Token transfers đến receivers sẽ được thực hiện ở bước riêng (Step 7)
              ...(extraTokenMint &&
                extraTokenMint.toString() !== PublicKey.default.toString() && {
                  buyerATA,
                  vaultAuthority,
                  vaultATA,
                  // receiverATAs - KHÔNG cần ở đây vì đây chỉ là tạo lixi
                }),
            },
            groupOptions,
          );

        const transaction = new Transaction().add(instruction);
        await this.prepareTransaction(transaction);

        // Log transaction details for debugging
        console.log('[buyTickets] Transaction details:', {
          instructions: transaction.instructions.length,
          feePayer: transaction.feePayer?.toString(),
          recentBlockhash:
            transaction.recentBlockhash?.toString().substring(0, 8) + '...',
        });

        // Log all accounts in the instruction
        if (transaction.instructions.length > 0) {
          const accounts = transaction.instructions[0].keys || [];
          console.log('[buyTickets] Instruction accounts:', {
            count: accounts.length,
            accounts: accounts.map((acc: any, idx: number) => ({
              index: idx,
              pubkey: acc.pubkey.toString(),
              isSigner: acc.isSigner,
              isWritable: acc.isWritable,
            })),
          });
        }

        // Validate transaction size (Solana limit is 1232 bytes)
        try {
          const serialized = transaction.serialize({
            requireAllSignatures: false,
            verifySignatures: false,
          });
          const txSize = serialized.length;
          console.log('[buyTickets] Transaction size:', txSize, 'bytes');

          if (txSize > 1232) {
            console.warn(
              '[buyTickets] Transaction size exceeds Solana limit (1232 bytes):',
              txSize,
            );
          }
        } catch (serializeError) {
          console.warn(
            '[buyTickets] Could not serialize transaction for size check:',
            serializeError,
          );
        }

        console.log(
          '[buyTickets] Transaction prepared, sending to wallet for user approval...',
        );

        // Optional: simulate first to capture on-chain error logs before wallet popup
        try {
          const sim = await this.connection.simulateTransaction(transaction, {
            sigVerify: false,
          } as any);
          if (sim?.value?.err) {
            console.warn('[buyTickets] Simulation error:', sim.value.err);
            if (Array.isArray(sim.value.logs)) {
              console.warn('[buyTickets] Simulation logs:', sim.value.logs);
            }
          }
        } catch (simError) {
          console.debug(
            '[buyTickets] Simulation failed (non-blocking):',
            simError,
          );
        }

        // This will trigger wallet popup - user MUST approve in Phantom wallet
        // Use signAndSendTransaction for Phantom wallet (recommended API)
        let buySignature: string;

        if (typeof wallet.signAndSendTransaction === 'function') {
          // Phantom wallet API (recommended method)
          // signAndSendTransaction automatically signs and sends, opens popup for user approval
          console.log(
            '[buyTickets] Using signAndSendTransaction (Phantom API)',
          );
          try {
            const result = await wallet.signAndSendTransaction(transaction);
            buySignature = result.signature;
          } catch (txError: any) {
            console.error(
              '[buyTickets] signAndSendTransaction error:',
              txError,
            );
            console.error('[buyTickets] Error details:', {
              name: txError?.name,
              message: txError?.message,
              code: txError?.code,
              stack: txError?.stack,
              toString: txError?.toString?.(),
            });

            // Try to extract more details from Phantom error
            let errorMessage =
              'User rejected transaction or transaction failed';
            if (txError?.message) {
              errorMessage = txError.message;
            } else if (typeof txError === 'string') {
              errorMessage = txError;
            } else if (txError?.toString) {
              errorMessage = txError.toString();
            }

            throw new Error(errorMessage);
          }
        } else if (typeof wallet.sendTransaction === 'function') {
          // Fallback for older wallet implementations
          console.log('[buyTickets] Using sendTransaction (fallback)');
          buySignature = await wallet.sendTransaction(
            transaction,
            this.connection,
          );
        } else {
          console.error(
            '[buyTickets] Wallet methods available:',
            Object.keys(wallet || {}),
          );
          throw new Error(
            'Wallet does not support transaction sending. ' +
              'Please ensure Phantom wallet is properly installed and connected.',
          );
        }

        console.log('[buyTickets] Transaction sent, signature:', buySignature);
        console.log('[buyTickets] Waiting for confirmation...');

        await this.connection.confirmTransaction(buySignature, 'confirmed');

        console.log('[buyTickets] Transaction confirmed!');

        devLog('✅ Tickets purchased:', buySignature);

        // ✅ Transfer tokens vào vault sau khi buy_tickets_auto thành công (theo test case line 617-628)
        // Nếu có extra token, cần transfer từ buyer ATA vào vault ATA
        if (
          extraTokenMint &&
          params.extraTokenAmount &&
          params.extraTokenAmount > 0
        ) {
          try {
            console.log('[buyTickets] Transferring extra tokens to vault...', {
              from: buyerATA.toString(),
              to: vaultATA.toString(),
              amount: params.extraTokenAmount,
            });

            // Import SPL token transfer instruction and ATA creation
            const {
              createTransferInstruction,
              createAssociatedTokenAccountInstruction,
              TOKEN_PROGRAM_ID,
              ASSOCIATED_TOKEN_PROGRAM_ID,
            } = await import('@solana/spl-token');

            // Check if vault ATA exists, create if needed
            const vaultAtaInfo = await this.connection.getAccountInfo(vaultATA);
            const transferInstructions: TransactionInstruction[] = [];

            if (!vaultAtaInfo) {
              console.log(
                '[buyTickets] Vault ATA does not exist, creating it...',
              );
              // Create vault ATA instruction
              // Note: vaultAuthority is a PDA, so we need to use createAssociatedTokenAccountInstruction
              // with allowOwnerOffCurve = true (handled by the instruction itself)
              const createVaultAtaIx = createAssociatedTokenAccountInstruction(
                wallet.publicKey, // Payer
                vaultATA, // ATA address (already derived)
                vaultAuthority, // Owner (PDA, may be off-curve)
                extraTokenMint, // Mint
              );
              transferInstructions.push(createVaultAtaIx);
            }

            // Create transfer instruction: from buyer ATA to vault ATA
            const transferIx = createTransferInstruction(
              buyerATA,
              vaultATA,
              wallet.publicKey,
              params.extraTokenAmount, // Amount in raw format (smallest unit)
              [],
              TOKEN_PROGRAM_ID,
            );
            transferInstructions.push(transferIx);

            // Create and send transfer transaction
            const transferTx = new Transaction().add(...transferInstructions);
            await this.prepareTransaction(transferTx);

            let transferSignature: string;
            if (typeof wallet.signAndSendTransaction === 'function') {
              console.log(
                '[buyTickets] Using signAndSendTransaction for token transfer',
              );
              const result = await wallet.signAndSendTransaction(transferTx);
              transferSignature = result.signature;
            } else {
              console.log(
                '[buyTickets] Using sendTransaction for token transfer',
              );
              transferSignature = await wallet.sendTransaction(
                transferTx,
                this.connection,
              );
            }

            await this.connection.confirmTransaction(
              transferSignature,
              'confirmed',
            );
            console.log(
              '[buyTickets] Token transfer confirmed:',
              transferSignature,
            );
            devLog('✅ Extra tokens transferred to vault:', transferSignature);
          } catch (transferError) {
            console.error(
              '[buyTickets] Failed to transfer tokens to vault:',
              transferError,
            );
            // Don't fail the entire operation - tickets were already purchased
            // User can transfer tokens manually later if needed
            console.warn(
              '[buyTickets] Token transfer failed, but tickets were purchased successfully. User can transfer tokens manually.',
            );
          }
        }

        const slot = await this.connection.getSlot();

        devLog('✅ Complete flow successful:', buySignature);

        return {
          success: true,
          hash: buySignature,
          signature: buySignature,
          slot: slot,
        };
      } else {
        // No extra token - use SOL only
        const instruction =
          await solanaProgramClient.createBuyTicketsInstruction(
            wallet,
            {
              groupId,
              numTickets: params.quantity,
              extraTokenMint: undefined,
              extraTokenAmount: 0,
              receivers,
            },
            groupOptions,
          );

        const transaction = new Transaction().add(instruction);
        await this.prepareTransaction(transaction);
        const signature = await wallet.sendTransaction(
          transaction,
          this.connection,
        );
        await this.connection.confirmTransaction(signature, 'confirmed');

        const slot = await this.connection.getSlot();

        devLog('Solana buyTickets transaction successful:', signature);

        return {
          success: true,
          hash: signature,
          signature: signature,
          slot: slot,
        };
      }
    } catch (error) {
      console.error('Failed to buy tickets:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async claimLixi(params: {
    group: GroupIdentifier;
    ticketId?: string;
  }): Promise<TransactionResult> {
    try {
      devLog('Solana claimLixi called with params:', params);

      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      const groupParams = this.ensureAutoGroupIdentifier(params.group, {
        requireGroupId: true,
      });

      // Parse string groupId to number or bigint first
      const { parseGroupIdFromString, groupIdToNumber } = await import(
        '../solana/pda'
      );
      let groupId: number;
      try {
        const parsed = parseGroupIdFromString(groupParams.groupId as string);
        groupId = groupIdToNumber(parsed);
      } catch (error: any) {
        // If group_id is too large, we can't use getGroupState with number
        // For now, throw error - getGroupState needs to be updated to support large numbers
        throw new Error(
          `group_id ${groupParams.groupId} is too large. Please update getGroupState to support large numbers.`,
        );
      }
      const groupOptions = {
        blockhashHash: groupParams.blockhashHash,
        buyer: groupParams.buyerPublicKey,
      };

      // Fetch group state to get extra token mint (if any) and derive ticket ID
      // Use getGroupStateByBuyerAndBlockhash for buy_tickets_auto pattern
      const groupState =
        await solanaProgramClient.getGroupStateByBuyerAndBlockhash(
          groupParams.buyerPublicKey,
          groupParams.blockhashHash,
        );
      
      // Derive ticket ID: startTicketId + claimed
      const toNumberSafe = (value: any, label: string): number => {
        if (typeof value === 'number') {
          return value;
        }
        if (typeof value === 'bigint') {
          return Number(value);
        }
        if (value && typeof value === 'object') {
          if (typeof value.toNumber === 'function') {
            try {
              return value.toNumber();
            } catch (err: any) {
              devLog(`[claimLixiByOwner] ${label}.toNumber failed`, err);
              const asString = value.toString?.();
              const parsed = asString ? Number(asString) : NaN;
              if (!Number.isNaN(parsed)) {
                return parsed;
              }
            }
          }
          if ('_bn' in value && typeof value._bn?.toString === 'function') {
            try {
              const bnString = value._bn.toString(10);
              const parsed = Number(bnString);
              if (!Number.isNaN(parsed)) {
                return parsed;
              }
            } catch (err: any) {
              devLog(`[claimLixiByOwner] ${label}._bn.toString failed`, err);
            }
          }
        }
        if (typeof value === 'string') {
          const parsed = Number(value);
          if (!Number.isNaN(parsed)) {
            return parsed;
          }
        }
        return 0;
      };

      const startTicketIdRaw =
        groupState?.startTicketId ?? groupState?.start_ticket_id;
      const claimedRaw = groupState?.claimed ?? groupState?.claimed_tickets;

      const previewToNumber = (value: any) => {
        try {
          if (value && typeof value.toNumber === 'function') {
            return value.toNumber();
          }
        } catch (err: any) {
          return `error:${err?.message || err}`;
        }
        return undefined;
      };
      const previewToString = (value: any) => {
        try {
          if (value && typeof value.toString === 'function') {
            return value.toString();
          }
        } catch (err: any) {
          return `error:${err?.message || err}`;
        }
        return undefined;
      };

      console.warn('[claimLixiByOwner] raw values', {
        startTicketIdRaw,
        claimedRaw,
        startTicketIdType: typeof startTicketIdRaw,
        claimedType: typeof claimedRaw,
        hasStartTicketToNumber:
          !!startTicketIdRaw && typeof startTicketIdRaw.toNumber === 'function',
        hasClaimedToNumber:
          !!claimedRaw && typeof claimedRaw.toNumber === 'function',
        startTicketIdToNumber: previewToNumber(startTicketIdRaw),
        startTicketIdToString: previewToString(startTicketIdRaw),
        claimedToNumber: previewToNumber(claimedRaw),
        claimedToString: previewToString(claimedRaw),
      });

      const startTicketId = toNumberSafe(startTicketIdRaw, 'startTicketId');
      const claimed = toNumberSafe(claimedRaw, 'claimed');
      const ticketId = startTicketId + claimed;
      console.warn('[claimLixiByOwner] ticket context', {
        startTicketId,
        claimed,
        ticketId,
      });
      
      // Derive NFT accounts from ticket ID
      const { solanaPDA } = await import('../solana/pda');
      const [nftMint] = solanaPDA.getNFTMintPDA(ticketId);
      console.warn(
        '[claimLixiByOwner] expected nft mint',
        nftMint.toBase58(),
      );
      const { getNFTMetadataPDA, getNFTMasterEditionPDA } = await import('../../utils/solana-collection');
      const nftMetadata = getNFTMetadataPDA(nftMint);
      const nftMasterEdition = getNFTMasterEditionPDA(nftMint);
      const { getAssociatedTokenAddressSync } = await import('@solana/spl-token');
      const receiverNftTokenAccount = getAssociatedTokenAddressSync(nftMint, wallet.publicKey);
      
      // Try multiple field names per adapter conventions
      const extraTokenMintStr =
        groupState?.extraTokenMint?.toString?.() ||
        groupState?.extra_token_mint?.toString?.() ||
        groupState?.localJackpotToken?.toString?.() ||
        undefined;

      let extraTokenMintPk: PublicKey | undefined;
      try {
        if (extraTokenMintStr) {
          const pk = new PublicKey(extraTokenMintStr);
          // Ignore default pubkey
          if (pk.toString() !== PublicKey.default.toString()) {
            extraTokenMintPk = pk;
          }
        }
      } catch (_) {
        // ignore invalid mint
      }

      // Derive SPL accounts when extra token exists
      let receiverTokenAta: PublicKey | undefined;
      let vaultAuthority: PublicKey | undefined;
      let tokenVaultAta: PublicKey | undefined;

      if (extraTokenMintPk) {
        const {
          getAssociatedTokenAddress,
          ASSOCIATED_TOKEN_PROGRAM_ID,
          TOKEN_PROGRAM_ID,
          createAssociatedTokenAccountInstruction,
        } = await import('@solana/spl-token');

        // Receiver ATA (ensure exists)
        receiverTokenAta = await getAssociatedTokenAddress(
          extraTokenMintPk,
          wallet.publicKey,
        );

        // ✅ Derive vault authority PDA và token vault ATA (off-curve-safe)
        // Pattern: [b"vault", buyer, blockhash_hash, mint] - matches Rust program
        const [pda] = solanaPDA.getExtraTokenVaultPDA(
          groupParams.buyerPublicKey,
          groupParams.blockhashHash,
          extraTokenMintPk,
        );
        vaultAuthority = pda;
        try {
          tokenVaultAta = await getAssociatedTokenAddress(
            extraTokenMintPk,
            vaultAuthority,
          );
        } catch (ataError: any) {
          if (
            ataError?.message?.includes('OffCurve') ||
            ataError?.name === 'TokenOwnerOffCurveError'
          ) {
            const [manualAta] = PublicKey.findProgramAddressSync(
              [
                vaultAuthority.toBuffer(),
                TOKEN_PROGRAM_ID.toBuffer(),
                extraTokenMintPk.toBuffer(),
              ],
              ASSOCIATED_TOKEN_PROGRAM_ID,
            );
            tokenVaultAta = manualAta;
          } else {
            throw ataError;
          }
        }

        // Create receiver ATA if missing (prepend ix)
        const receiverAtaInfo = await this.connection.getAccountInfo(
          receiverTokenAta,
        );
        if (!receiverAtaInfo) {
          const createAtaIx = createAssociatedTokenAccountInstruction(
            wallet.publicKey,
            receiverTokenAta,
            wallet.publicKey,
            extraTokenMintPk,
          );
          // Build claim instruction with SPL accounts and NFT accounts after ensuring ATA
          const claimIx = await solanaProgramClient.createClaimLixiInstruction(
            wallet,
            groupId,
            groupOptions,
            {
              extraTokenMint: extraTokenMintPk,
              vaultAuthority,
              tokenVaultAta,
              receiverTokenAta,
              ticketId,
              nftMint,
              nftMetadata,
              nftMasterEdition,
              receiverNftTokenAccount,
            },
          );
          const tx = new Transaction().add(createAtaIx, claimIx);
          await this.prepareTransaction(tx);

          let sig: string;
          if (typeof wallet.signAndSendTransaction === 'function') {
            const res = await wallet.signAndSendTransaction(tx);
            sig = res.signature;
          } else {
            sig = await wallet.sendTransaction(tx, this.connection);
          }
          await this.connection.confirmTransaction(sig, 'confirmed');
          const slot = await this.connection.getSlot();
          devLog('Solana claimLixi transaction successful:', sig);
          return { success: true, hash: sig, signature: sig, slot };
        }
      }

      // Create instruction with SPL accounts and NFT accounts if available
      const instruction = await solanaProgramClient.createClaimLixiInstruction(
        wallet,
        groupId,
        groupOptions,
        {
          ...(extraTokenMintPk && vaultAuthority && tokenVaultAta && receiverTokenAta
            ? {
                extraTokenMint: extraTokenMintPk,
                vaultAuthority,
                tokenVaultAta,
                receiverTokenAta,
              }
            : {}),
          ticketId,
          nftMint,
          nftMetadata,
          nftMasterEdition,
          receiverNftTokenAccount,
        },
      );

      // Create transaction and send
      const transaction = new Transaction().add(instruction);
      await this.prepareTransaction(transaction);

      let signature: string;
      if (typeof wallet.signAndSendTransaction === 'function') {
        const res = await wallet.signAndSendTransaction(transaction);
        signature = res.signature;
      } else {
        signature = await wallet.sendTransaction(transaction, this.connection);
      }

      await this.connection.confirmTransaction(signature, 'confirmed');
      const slot = await this.connection.getSlot();
      devLog('Solana claimLixi transaction successful:', signature);
      return { success: true, hash: signature, signature, slot };
    } catch (error) {
      console.error('Failed to claim lixi:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async claimLixiByOwner(params: {
    group: GroupIdentifier;
    receiver: string;
  }): Promise<TransactionResult> {
    try {
      devLog('Solana claimLixiByOwner called with params:', params);

      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      const groupParams = this.ensureAutoGroupIdentifier(params.group, {
        requireGroupId: true,
      });
      let receiverPublicKey: PublicKey;
      try {
        receiverPublicKey = new PublicKey(params.receiver);
      } catch (error) {
        throw new Error(`Invalid receiver address: ${params.receiver}`);
      }

      // Parse string groupId to number or bigint first
      const { parseGroupIdFromString, groupIdToNumber } = await import(
        '../solana/pda'
      );
      let groupId: number;
      try {
        const parsed = parseGroupIdFromString(groupParams.groupId as string);
        groupId = groupIdToNumber(parsed);
      } catch (error: any) {
        // If group_id is too large, we can't use getGroupState with number
        // For now, throw error - getGroupState needs to be updated to support large numbers
        throw new Error(
          `group_id ${groupParams.groupId} is too large. Please update getGroupState to support large numbers.`,
        );
      }
      const groupOptions = {
        blockhashHash: groupParams.blockhashHash,
        buyer: groupParams.buyerPublicKey,
      };

      // Verify wallet is the owner (buyer)
      if (!wallet.publicKey.equals(groupParams.buyerPublicKey)) {
        throw new Error(
          'Wallet must be the group owner (buyer) to claim lixi by owner',
        );
      }

      // Fetch group state to get extra token mint (if any) and derive ticket ID
      // Use getGroupStateByBuyerAndBlockhash for buy_tickets_auto pattern
      const groupState =
        await solanaProgramClient.getGroupStateByBuyerAndBlockhash(
          groupParams.buyerPublicKey,
          groupParams.blockhashHash,
        );
      
      // Derive ticket ID: startTicketId + claimed
      const startTicketId = groupState?.startTicketId?.toNumber?.() ?? 
        (typeof groupState?.startTicketId === 'number' ? groupState.startTicketId : 0) ??
        (typeof groupState?.start_ticket_id === 'number' ? groupState.start_ticket_id : 0) ??
        0;
      const claimed = groupState?.claimed?.toNumber?.() ?? 
        (typeof groupState?.claimed === 'number' ? groupState.claimed : 0) ??
        0;
      const ticketId = startTicketId + claimed;
      devLog('[claimLixiByOwner] ticket context', {
        startTicketId,
        claimed,
        ticketId,
      });
      
      // Derive NFT accounts from ticket ID
      const { solanaPDA } = await import('../solana/pda');
      const [nftMint] = solanaPDA.getNFTMintPDA(ticketId);
      devLog('[claimLixiByOwner] expected nft mint', nftMint.toBase58());
      const { getNFTMetadataPDA, getNFTMasterEditionPDA } = await import('../../utils/solana-collection');
      const nftMetadata = getNFTMetadataPDA(nftMint);
      const nftMasterEdition = getNFTMasterEditionPDA(nftMint);
      const { getAssociatedTokenAddressSync } = await import('@solana/spl-token');
      const receiverNftTokenAccount = getAssociatedTokenAddressSync(nftMint, receiverPublicKey);
      
      // Metadata will be created automatically on-chain if missing
      
      // Try multiple field names per adapter conventions
      const extraTokenMintStr =
        groupState?.extraTokenMint?.toString?.() ||
        groupState?.extra_token_mint?.toString?.() ||
        groupState?.localJackpotToken?.toString?.() ||
        undefined;

      let extraTokenMintPk: PublicKey | undefined;
      try {
        if (extraTokenMintStr) {
          const pk = new PublicKey(extraTokenMintStr);
          // Ignore default pubkey
          if (pk.toString() !== PublicKey.default.toString()) {
            extraTokenMintPk = pk;
          }
        }
      } catch (_) {
        // ignore invalid mint
      }

      // Derive SPL accounts when extra token exists
      let receiverTokenAta: PublicKey | undefined;
      let vaultAuthority: PublicKey | undefined;
      let tokenVaultAta: PublicKey | undefined;

      if (extraTokenMintPk) {
        const {
          getAssociatedTokenAddress,
          ASSOCIATED_TOKEN_PROGRAM_ID,
          TOKEN_PROGRAM_ID,
          createAssociatedTokenAccountInstruction,
        } = await import('@solana/spl-token');

        // Receiver ATA (ensure exists) - owner is the receiver
        receiverTokenAta = await getAssociatedTokenAddress(
          extraTokenMintPk,
          receiverPublicKey,
        );

        // ✅ Derive vault authority PDA và token vault ATA (off-curve-safe)
        // Pattern: [b"vault", buyer, blockhash_hash, mint] - matches Rust program
        const [pda] = solanaPDA.getExtraTokenVaultPDA(
          groupParams.buyerPublicKey,
          groupParams.blockhashHash,
          extraTokenMintPk,
        );
        vaultAuthority = pda;
        try {
          tokenVaultAta = await getAssociatedTokenAddress(
            extraTokenMintPk,
            vaultAuthority,
          );
        } catch (ataError: any) {
          if (
            ataError?.message?.includes('OffCurve') ||
            ataError?.name === 'TokenOwnerOffCurveError'
          ) {
            const [manualAta] = PublicKey.findProgramAddressSync(
              [
                vaultAuthority.toBuffer(),
                TOKEN_PROGRAM_ID.toBuffer(),
                extraTokenMintPk.toBuffer(),
              ],
              ASSOCIATED_TOKEN_PROGRAM_ID,
            );
            tokenVaultAta = manualAta;
          } else {
            throw ataError;
          }
        }

        // Create receiver ATA if missing (prepend ix)
        const receiverAtaInfo = await this.connection.getAccountInfo(
          receiverTokenAta,
        );
        if (!receiverAtaInfo) {
          const createAtaIx = createAssociatedTokenAccountInstruction(
            wallet.publicKey,
            receiverTokenAta,
            receiverPublicKey,
            extraTokenMintPk,
          );
          // Build claim instruction with SPL accounts and NFT accounts after ensuring ATA
          const claimIx =
            await solanaProgramClient.createClaimLixiByOwnerInstruction(
              wallet,
              groupId,
              {
                receiver: receiverPublicKey,
                groupOptions,
              },
              {
                extraTokenMint: extraTokenMintPk,
                vaultAuthority,
                tokenVaultAta,
                receiverTokenAta,
                ticketId,
                nftMint,
                nftMetadata,
                nftMasterEdition,
                receiverNftTokenAccount,
              },
            );
          const tx = new Transaction().add(createAtaIx, claimIx);
          await this.prepareTransaction(tx);
          await this.debugSimulateTransaction(
            tx,
            'claimLixiByOwner::createAta+claim',
          );

          let sig: string;
          if (typeof wallet.signAndSendTransaction === 'function') {
            const res = await wallet.signAndSendTransaction(tx);
            sig = res.signature;
          } else {
            sig = await wallet.sendTransaction(tx, this.connection);
          }
          await this.connection.confirmTransaction(sig, 'confirmed');
          const slot = await this.connection.getSlot();
          devLog('Solana claimLixiByOwner transaction successful:', sig);
          return { success: true, hash: sig, signature: sig, slot };
        }
      }

      // Create instruction with SPL accounts and NFT accounts if available
      const instruction =
        await solanaProgramClient.createClaimLixiByOwnerInstruction(
          wallet,
          groupId,
          {
            receiver: receiverPublicKey,
            groupOptions,
          },
          {
            ...(extraTokenMintPk &&
              vaultAuthority &&
              tokenVaultAta &&
              receiverTokenAta
              ? {
                  extraTokenMint: extraTokenMintPk,
                  vaultAuthority,
                  tokenVaultAta,
                  receiverTokenAta,
                }
              : {}),
            ticketId,
            nftMint,
            nftMetadata,
            nftMasterEdition,
            receiverNftTokenAccount,
          },
        );

      // Create transaction and send
      const transaction = new Transaction().add(instruction);
      await this.prepareTransaction(transaction);
      await this.debugSimulateTransaction(
        transaction,
        'claimLixiByOwner::claimOnly',
      );

      let signature: string;
      if (typeof wallet.signAndSendTransaction === 'function') {
        const res = await wallet.signAndSendTransaction(transaction);
        signature = res.signature;
      } else {
        signature = await wallet.sendTransaction(transaction, this.connection);
      }

      await this.connection.confirmTransaction(signature, 'confirmed');
      const slot = await this.connection.getSlot();
      devLog('Solana claimLixiByOwner transaction successful:', signature);
      return { success: true, hash: signature, signature, slot };
    } catch (error) {
      console.error('Failed to claim lixi by owner:', error);
      if (error instanceof SendTransactionError) {
        console.error(
          '[claimLixiByOwner] RPC logs:',
          error.logs ?? 'No RPC logs available',
        );
      }
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async claimManyByOwner(params: {
    group: GroupIdentifier;
    receivers: string[];
  }): Promise<TransactionResult> {
    try {
      devLog('Solana claimManyByOwner called with params:', params);

      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      const groupParams = this.ensureAutoGroupIdentifier(params.group, {
        requireGroupId: true,
      });

      // Parse string groupId to number or bigint first
      const { parseGroupIdFromString, groupIdToNumber } = await import(
        '../solana/pda'
      );
      let groupId: number;
      try {
        const parsed = parseGroupIdFromString(groupParams.groupId as string);
        groupId = groupIdToNumber(parsed);
      } catch (error: any) {
        throw new Error(
          `group_id ${groupParams.groupId} is too large. Please update claimManyByOwner to support large numbers.`,
        );
      }
      const groupOptions = {
        blockhashHash: groupParams.blockhashHash,
        buyer: groupParams.buyerPublicKey,
      };

      // Verify wallet is the owner (buyer)
      if (!wallet.publicKey.equals(groupParams.buyerPublicKey)) {
        throw new Error(
          'Wallet must be the group owner (buyer) to claim many by owner',
        );
      }

      // Validate receivers (1-5)
      if (!params.receivers || params.receivers.length === 0) {
        throw new Error('Receivers array cannot be empty');
      }
      if (params.receivers.length > 5) {
        throw new Error('Cannot claim for more than 5 receivers at once');
      }

      const numReceivers = params.receivers.length;

      // Parse receiver addresses
      const receiverPks: PublicKey[] = [];
      for (const addr of params.receivers) {
        try {
          if (!addr || typeof addr !== 'string') {
            throw new Error(`Invalid receiver address: ${addr}`);
          }
          const pk = new PublicKey(addr);
          receiverPks.push(pk);
        } catch (error) {
          throw new Error(
            `Invalid receiver address: ${addr} - ${
              error instanceof Error ? error.message : 'Unknown error'
            }`,
          );
        }
      }

      // Fetch global state for validation
      const globalState = await solanaProgramClient.getGlobalState();
      if (!globalState) {
        throw new Error('Global state not found');
      }

      // Validate closing time has NOT passed (must claim before closing time)
      const closingTime =
        globalState?.closingTime?.toNumber?.() ||
        globalState?.closing_time?.toNumber?.() ||
        0;
      const currentTime = Math.floor(Date.now() / 1000);
      if (currentTime >= closingTime) {
        throw new Error(
          `Closing time has passed. Current: ${currentTime}, Closing: ${closingTime}. Cannot claim many by owner after closing time.`,
        );
      }

      // Fetch group state to check available tickets
      const groupState =
        await solanaProgramClient.getGroupStateByBuyerAndBlockhash(
          groupParams.buyerPublicKey,
          groupParams.blockhashHash,
        );

      const size = groupState?.size || groupState?.groupSize || 0;
      const claimed = groupState?.claimed || groupState?.groupClaimed || 0;
      const availableTickets = size - claimed;

      if (availableTickets < numReceivers) {
        throw new Error(
          `Not enough tickets available. Available: ${availableTickets}, Requested: ${numReceivers}`,
        );
      }

      // Derive ticket IDs for all receivers: startTicketId + claimed + i
      const startTicketId = groupState?.startTicketId?.toNumber?.() ?? 
        (typeof groupState?.startTicketId === 'number' ? groupState.startTicketId : 0) ??
        (typeof groupState?.start_ticket_id === 'number' ? groupState.start_ticket_id : 0) ??
        0;
      const claimedCount = groupState?.claimed?.toNumber?.() ?? 
        (typeof groupState?.claimed === 'number' ? groupState.claimed : 0) ??
        0;
      
      const ticketIds: number[] = [];
      const nftMints: PublicKey[] = [];
      const nftMetadatas: PublicKey[] = [];
      const nftMasterEditions: PublicKey[] = [];
      const receiverNftTokenAccounts: PublicKey[] = [];
      
      const { solanaPDA } = await import('../solana/pda');
      const { getNFTMetadataPDA, getNFTMasterEditionPDA } = await import('../../utils/solana-collection');
      const { getAssociatedTokenAddressSync } = await import('@solana/spl-token');
      
      for (let i = 0; i < numReceivers; i++) {
        const ticketId = startTicketId + claimedCount + i;
        ticketIds.push(ticketId);
        
        const [nftMint] = solanaPDA.getNFTMintPDA(ticketId);
        nftMints.push(nftMint);
        nftMetadatas.push(getNFTMetadataPDA(nftMint));
        nftMasterEditions.push(getNFTMasterEditionPDA(nftMint));
        receiverNftTokenAccounts.push(getAssociatedTokenAddressSync(nftMint, receiverPks[i]));
        
        // Metadata will be created automatically on-chain if missing
      }

      // Check if group has extra token mint
      const extraTokenMintStr =
        groupState?.extraTokenMint?.toString?.() ||
        groupState?.extra_token_mint?.toString?.() ||
        undefined;

      let extraTokenMintPk: PublicKey | undefined;
      try {
        if (extraTokenMintStr) {
          const pk = new PublicKey(extraTokenMintStr);
          if (pk.toString() !== PublicKey.default.toString()) {
            extraTokenMintPk = pk;
          }
        }
      } catch (_) {
        // ignore invalid mint
      }

      // Derive SPL accounts when extra token exists
      let vaultAuthority: PublicKey | undefined;
      let tokenVaultAta: PublicKey | undefined;
      let receiverTokenAtas: PublicKey[] | undefined;

      if (extraTokenMintPk) {
        const {
          getAssociatedTokenAddress,
          ASSOCIATED_TOKEN_PROGRAM_ID,
          TOKEN_PROGRAM_ID,
        } = await import('@solana/spl-token');

        // ✅ Derive vault authority PDA và token vault ATA (off-curve-safe)
        // Pattern: [b"vault", buyer, blockhash_hash, mint] - matches Rust program
        const [pda] = solanaPDA.getExtraTokenVaultPDA(
          groupParams.buyerPublicKey,
          groupParams.blockhashHash,
          extraTokenMintPk,
        );
        vaultAuthority = pda;
        try {
          tokenVaultAta = await getAssociatedTokenAddress(
            extraTokenMintPk,
            vaultAuthority,
          );
        } catch (ataError: any) {
          if (
            ataError?.message?.includes('OffCurve') ||
            ataError?.name === 'TokenOwnerOffCurveError'
          ) {
            const [manualAta] = PublicKey.findProgramAddressSync(
              [
                vaultAuthority.toBuffer(),
                TOKEN_PROGRAM_ID.toBuffer(),
                extraTokenMintPk.toBuffer(),
              ],
              ASSOCIATED_TOKEN_PROGRAM_ID,
            );
            tokenVaultAta = manualAta;
          } else {
            throw ataError;
          }
        }

        // Derive receiver token ATAs
        receiverTokenAtas = [];
        for (const receiverPk of receiverPks) {
          try {
            const receiverAta = await getAssociatedTokenAddress(
              extraTokenMintPk,
              receiverPk,
            );
            receiverTokenAtas.push(receiverAta);
          } catch (ataError: any) {
            if (
              ataError?.message?.includes('OffCurve') ||
              ataError?.name === 'TokenOwnerOffCurveError'
            ) {
              const [manualAta] = PublicKey.findProgramAddressSync(
                [
                  receiverPk.toBuffer(),
                  TOKEN_PROGRAM_ID.toBuffer(),
                  extraTokenMintPk.toBuffer(),
                ],
                ASSOCIATED_TOKEN_PROGRAM_ID,
              );
              receiverTokenAtas.push(manualAta);
            } else {
              throw ataError;
            }
          }
        }
      }

      // Create instruction with NFT accounts
      const instruction =
        await solanaProgramClient.createClaimManyByOwnerInstruction(
          wallet,
          groupId,
          numReceivers,
          receiverPks,
          groupOptions,
          {
            ...(extraTokenMintPk &&
              vaultAuthority &&
              tokenVaultAta &&
              receiverTokenAtas
              ? {
                  extraTokenMint: extraTokenMintPk,
                  vaultAuthority,
                  tokenVaultAta,
                  receiverTokenAtas,
                }
              : {}),
            ticketIds,
            nftMints,
            nftMetadatas,
            nftMasterEditions,
            receiverNftTokenAccounts,
          },
        );

      // Create transaction and send
      const transaction = new Transaction().add(instruction);
      await this.prepareTransaction(transaction);

      let signature: string;
      if (typeof wallet.signAndSendTransaction === 'function') {
        const res = await wallet.signAndSendTransaction(transaction);
        signature = res.signature;
      } else {
        signature = await wallet.sendTransaction(transaction, this.connection);
      }

      await this.connection.confirmTransaction(signature, 'confirmed');
      const slot = await this.connection.getSlot();
      devLog('Solana claimManyByOwner transaction successful:', signature);
      return { success: true, hash: signature, signature, slot };
    } catch (error) {
      console.error('Failed to claim many by owner:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async claimLixiByAdmin(params: {
    group: GroupIdentifier;
    receiver: string;
  }): Promise<TransactionResult> {
    try {
      devLog('Solana claimLixiByAdmin called with params:', params);

      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      const groupParams = this.ensureAutoGroupIdentifier(params.group, {
        requireGroupId: true,
      });

      // Parse string groupId to number or bigint first
      const { parseGroupIdFromString, groupIdToNumber } = await import(
        '../solana/pda'
      );
      let groupId: number;
      try {
        const parsed = parseGroupIdFromString(groupParams.groupId as string);
        groupId = groupIdToNumber(parsed);
      } catch (error: any) {
        throw new Error(
          `group_id ${groupParams.groupId} is too large. Please update claimLixiByAdmin to support large numbers.`,
        );
      }
      const groupOptions = {
        blockhashHash: groupParams.blockhashHash,
        buyer: groupParams.buyerPublicKey,
      };

      // Parse receiver address
      let receiverPk: PublicKey;
      try {
        if (!params.receiver || typeof params.receiver !== 'string') {
          throw new Error(`Invalid receiver address: ${params.receiver}`);
        }
        receiverPk = new PublicKey(params.receiver);
      } catch (error) {
        throw new Error(
          `Invalid receiver address: ${params.receiver} - ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
        );
      }

      // Fetch global state for validation
      const globalState = await solanaProgramClient.getGlobalState();
      if (!globalState) {
        throw new Error('Global state not found');
      }

      // Validate closing time has NOT passed (must claim before closing time)
      const closingTime =
        globalState?.closingTime?.toNumber?.() ||
        globalState?.closing_time?.toNumber?.() ||
        0;
      const currentTime = Math.floor(Date.now() / 1000);
      if (currentTime >= closingTime) {
        throw new Error(
          `Closing time has passed. Current: ${currentTime}, Closing: ${closingTime}. Cannot claim lixi by admin after closing time.`,
        );
      }

      // Fetch group state to check available tickets
      const groupState =
        await solanaProgramClient.getGroupStateByBuyerAndBlockhash(
          groupParams.buyerPublicKey,
          groupParams.blockhashHash,
        );

      const size = groupState?.size || groupState?.groupSize || 0;
      const claimed = groupState?.claimed || groupState?.groupClaimed || 0;

      if (claimed >= size) {
        throw new Error(
          `No tickets available to claim. All tickets have been claimed (${claimed}/${size}).`,
        );
      }

      // Derive ticket ID: startTicketId + claimed
      const startTicketId = groupState?.startTicketId?.toNumber?.() ?? 
        (typeof groupState?.startTicketId === 'number' ? groupState.startTicketId : 0) ??
        (typeof groupState?.start_ticket_id === 'number' ? groupState.start_ticket_id : 0) ??
        0;
      const claimedCount = groupState?.claimed?.toNumber?.() ?? 
        (typeof groupState?.claimed === 'number' ? groupState.claimed : 0) ??
        0;
      const ticketId = startTicketId + claimedCount;
      
      // Derive NFT accounts from ticket ID
      const { solanaPDA } = await import('../solana/pda');
      const [nftMint] = solanaPDA.getNFTMintPDA(ticketId);
      const { getNFTMetadataPDA, getNFTMasterEditionPDA } = await import('../../utils/solana-collection');
      const nftMetadata = getNFTMetadataPDA(nftMint);
      const nftMasterEdition = getNFTMasterEditionPDA(nftMint);
      const { getAssociatedTokenAddressSync } = await import('@solana/spl-token');
      const receiverNftTokenAccount = getAssociatedTokenAddressSync(nftMint, receiverPk);
      
      // Metadata will be created automatically on-chain if missing

      // Check if group has extra token mint
      const extraTokenMintStr =
        groupState?.extraTokenMint?.toString?.() ||
        groupState?.extra_token_mint?.toString?.() ||
        undefined;

      let extraTokenMintPk: PublicKey | undefined;
      try {
        if (extraTokenMintStr) {
          const pk = new PublicKey(extraTokenMintStr);
          if (pk.toString() !== PublicKey.default.toString()) {
            extraTokenMintPk = pk;
          }
        }
      } catch (_) {
        // ignore invalid mint
      }

      // Derive SPL accounts when extra token exists
      let vaultAuthority: PublicKey | undefined;
      let tokenVaultAta: PublicKey | undefined;
      let receiverTokenAta: PublicKey | undefined;

      if (extraTokenMintPk) {
        const {
          getAssociatedTokenAddress,
          ASSOCIATED_TOKEN_PROGRAM_ID,
          TOKEN_PROGRAM_ID,
          createAssociatedTokenAccountInstruction,
        } = await import('@solana/spl-token');

        // Receiver ATA (ensure exists)
        try {
          receiverTokenAta = await getAssociatedTokenAddress(
            extraTokenMintPk,
            receiverPk,
          );
        } catch (ataError: any) {
          if (
            ataError?.message?.includes('OffCurve') ||
            ataError?.name === 'TokenOwnerOffCurveError'
          ) {
            const [manualAta] = PublicKey.findProgramAddressSync(
              [
                receiverPk.toBuffer(),
                TOKEN_PROGRAM_ID.toBuffer(),
                extraTokenMintPk.toBuffer(),
              ],
              ASSOCIATED_TOKEN_PROGRAM_ID,
            );
            receiverTokenAta = manualAta;
          } else {
            throw ataError;
          }
        }

        // ✅ Derive vault authority PDA và token vault ATA (off-curve-safe)
        // Pattern: [b"vault", buyer, blockhash_hash, mint] - matches Rust program
        const [pda] = solanaPDA.getExtraTokenVaultPDA(
          groupParams.buyerPublicKey,
          groupParams.blockhashHash,
          extraTokenMintPk,
        );
        vaultAuthority = pda;
        try {
          tokenVaultAta = await getAssociatedTokenAddress(
            extraTokenMintPk,
            vaultAuthority,
          );
        } catch (ataError: any) {
          if (
            ataError?.message?.includes('OffCurve') ||
            ataError?.name === 'TokenOwnerOffCurveError'
          ) {
            const [manualAta] = PublicKey.findProgramAddressSync(
              [
                vaultAuthority.toBuffer(),
                TOKEN_PROGRAM_ID.toBuffer(),
                extraTokenMintPk.toBuffer(),
              ],
              ASSOCIATED_TOKEN_PROGRAM_ID,
            );
            tokenVaultAta = manualAta;
          } else {
            throw ataError;
          }
        }

        // Create receiver ATA if missing (prepend ix)
        const receiverAtaInfo = await this.connection.getAccountInfo(
          receiverTokenAta,
        );
        if (!receiverAtaInfo) {
          const createAtaIx = createAssociatedTokenAccountInstruction(
            wallet.publicKey, // Payer (admin)
            receiverTokenAta,
            receiverPk, // Owner
            extraTokenMintPk,
          );
          // Build claim instruction with SPL accounts and NFT accounts after ensuring ATA
          const claimIx =
            await solanaProgramClient.createClaimLixiByAdminInstruction(
              wallet,
              groupId,
              receiverPk,
              groupOptions,
              {
                extraTokenMint: extraTokenMintPk,
                vaultAuthority,
                tokenVaultAta,
                receiverTokenAta,
                ticketId,
                nftMint,
                nftMetadata,
                nftMasterEdition,
                receiverNftTokenAccount,
              },
            );
          const tx = new Transaction().add(createAtaIx, claimIx);
          await this.prepareTransaction(tx);

          let sig: string;
          if (typeof wallet.signAndSendTransaction === 'function') {
            const res = await wallet.signAndSendTransaction(tx);
            sig = res.signature;
          } else {
            sig = await wallet.sendTransaction(tx, this.connection);
          }
          await this.connection.confirmTransaction(sig, 'confirmed');
          const slot = await this.connection.getSlot();
          devLog('Solana claimLixiByAdmin transaction successful:', sig);
          return { success: true, hash: sig, signature: sig, slot };
        }
      }

      // Create instruction with SPL accounts and NFT accounts if available
      const instruction =
        await solanaProgramClient.createClaimLixiByAdminInstruction(
          wallet,
          groupId,
          receiverPk,
          groupOptions,
          {
            ...(extraTokenMintPk &&
              vaultAuthority &&
              tokenVaultAta &&
              receiverTokenAta
              ? {
                  extraTokenMint: extraTokenMintPk,
                  vaultAuthority,
                  tokenVaultAta,
                  receiverTokenAta,
                }
              : {}),
            ticketId,
            nftMint,
            nftMetadata,
            nftMasterEdition,
            receiverNftTokenAccount,
          },
        );

      // Create transaction and send
      const transaction = new Transaction().add(instruction);
      await this.prepareTransaction(transaction);

      let signature: string;
      if (typeof wallet.signAndSendTransaction === 'function') {
        const res = await wallet.signAndSendTransaction(transaction);
        signature = res.signature;
      } else {
        signature = await wallet.sendTransaction(transaction, this.connection);
      }

      await this.connection.confirmTransaction(signature, 'confirmed');
      const slot = await this.connection.getSlot();
      devLog('Solana claimLixiByAdmin transaction successful:', signature);
      return { success: true, hash: signature, signature, slot };
    } catch (error) {
      console.error('Failed to claim lixi by admin:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async claimManyByAdmin(params: {
    group: GroupIdentifier;
    receivers: string[];
  }): Promise<TransactionResult> {
    try {
      devLog('Solana claimManyByAdmin called with params:', params);

      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      const groupParams = this.ensureAutoGroupIdentifier(params.group, {
        requireGroupId: true,
      });

      // Parse string groupId to number or bigint first
      const { parseGroupIdFromString, groupIdToNumber } = await import(
        '../solana/pda'
      );
      let groupId: number;
      try {
        const parsed = parseGroupIdFromString(groupParams.groupId as string);
        groupId = groupIdToNumber(parsed);
      } catch (error: any) {
        throw new Error(
          `group_id ${groupParams.groupId} is too large. Please update claimManyByAdmin to support large numbers.`,
        );
      }
      const groupOptions = {
        blockhashHash: groupParams.blockhashHash,
        buyer: groupParams.buyerPublicKey,
      };

      // Validate receivers (1-5)
      if (!params.receivers || params.receivers.length === 0) {
        throw new Error('Receivers array cannot be empty');
      }
      if (params.receivers.length > 5) {
        throw new Error('Cannot claim for more than 5 receivers at once');
      }

      const numReceivers = params.receivers.length;

      // Parse receiver addresses
      const receiverPks: PublicKey[] = [];
      for (const addr of params.receivers) {
        try {
          if (!addr || typeof addr !== 'string') {
            throw new Error(`Invalid receiver address: ${addr}`);
          }
          const pk = new PublicKey(addr);
          receiverPks.push(pk);
        } catch (error) {
          throw new Error(
            `Invalid receiver address: ${addr} - ${
              error instanceof Error ? error.message : 'Unknown error'
            }`,
          );
        }
      }

      // Fetch global state for validation
      const globalState = await solanaProgramClient.getGlobalState();
      if (!globalState) {
        throw new Error('Global state not found');
      }

      // Validate closing time has NOT passed (must claim before closing time)
      const closingTime =
        globalState?.closingTime?.toNumber?.() ||
        globalState?.closing_time?.toNumber?.() ||
        0;
      const currentTime = Math.floor(Date.now() / 1000);
      if (currentTime >= closingTime) {
        throw new Error(
          `Closing time has passed. Current: ${currentTime}, Closing: ${closingTime}. Cannot claim many by admin after closing time.`,
        );
      }

      // Fetch group state to check available tickets
      const groupState =
        await solanaProgramClient.getGroupStateByBuyerAndBlockhash(
          groupParams.buyerPublicKey,
          groupParams.blockhashHash,
        );

      const size = groupState?.size || groupState?.groupSize || 0;
      const claimed = groupState?.claimed || groupState?.groupClaimed || 0;
      const availableTickets = size - claimed;

      if (availableTickets < numReceivers) {
        throw new Error(
          `Not enough tickets available. Available: ${availableTickets}, Requested: ${numReceivers}`,
        );
      }

      // Derive ticket IDs for all receivers: startTicketId + claimed + i
      const startTicketId = groupState?.startTicketId?.toNumber?.() ?? 
        (typeof groupState?.startTicketId === 'number' ? groupState.startTicketId : 0) ??
        (typeof groupState?.start_ticket_id === 'number' ? groupState.start_ticket_id : 0) ??
        0;
      const claimedCount = groupState?.claimed?.toNumber?.() ?? 
        (typeof groupState?.claimed === 'number' ? groupState.claimed : 0) ??
        0;
      
      const ticketIds: number[] = [];
      const nftMints: PublicKey[] = [];
      const nftMetadatas: PublicKey[] = [];
      const nftMasterEditions: PublicKey[] = [];
      const receiverNftTokenAccounts: PublicKey[] = [];
      
      const { solanaPDA } = await import('../solana/pda');
      const { getNFTMetadataPDA, getNFTMasterEditionPDA } = await import('../../utils/solana-collection');
      const { getAssociatedTokenAddressSync } = await import('@solana/spl-token');
      
      for (let i = 0; i < numReceivers; i++) {
        const ticketId = startTicketId + claimedCount + i;
        ticketIds.push(ticketId);
        
        const [nftMint] = solanaPDA.getNFTMintPDA(ticketId);
        nftMints.push(nftMint);
        nftMetadatas.push(getNFTMetadataPDA(nftMint));
        nftMasterEditions.push(getNFTMasterEditionPDA(nftMint));
        receiverNftTokenAccounts.push(getAssociatedTokenAddressSync(nftMint, receiverPks[i]));
        
        // Metadata will be created automatically on-chain if missing
      }

      // Check if group has extra token mint
      const extraTokenMintStr =
        groupState?.extraTokenMint?.toString?.() ||
        groupState?.extra_token_mint?.toString?.() ||
        undefined;

      let extraTokenMintPk: PublicKey | undefined;
      try {
        if (extraTokenMintStr) {
          const pk = new PublicKey(extraTokenMintStr);
          if (pk.toString() !== PublicKey.default.toString()) {
            extraTokenMintPk = pk;
          }
        }
      } catch (_) {
        // ignore invalid mint
      }

      // Derive SPL accounts when extra token exists
      let vaultAuthority: PublicKey | undefined;
      let tokenVaultAta: PublicKey | undefined;
      let receiverTokenAtas: PublicKey[] | undefined;

      if (extraTokenMintPk) {
        const {
          getAssociatedTokenAddress,
          ASSOCIATED_TOKEN_PROGRAM_ID,
          TOKEN_PROGRAM_ID,
        } = await import('@solana/spl-token');

        // ✅ Derive vault authority PDA và token vault ATA (off-curve-safe)
        // Pattern: [b"vault", buyer, blockhash_hash, mint] - matches Rust program
        const [pda] = solanaPDA.getExtraTokenVaultPDA(
          groupParams.buyerPublicKey,
          groupParams.blockhashHash,
          extraTokenMintPk,
        );
        vaultAuthority = pda;
        try {
          tokenVaultAta = await getAssociatedTokenAddress(
            extraTokenMintPk,
            vaultAuthority,
          );
        } catch (ataError: any) {
          if (
            ataError?.message?.includes('OffCurve') ||
            ataError?.name === 'TokenOwnerOffCurveError'
          ) {
            const [manualAta] = PublicKey.findProgramAddressSync(
              [
                vaultAuthority.toBuffer(),
                TOKEN_PROGRAM_ID.toBuffer(),
                extraTokenMintPk.toBuffer(),
              ],
              ASSOCIATED_TOKEN_PROGRAM_ID,
            );
            tokenVaultAta = manualAta;
          } else {
            throw ataError;
          }
        }

        // Derive receiver token ATAs
        receiverTokenAtas = [];
        for (const receiverPk of receiverPks) {
          try {
            const receiverAta = await getAssociatedTokenAddress(
              extraTokenMintPk,
              receiverPk,
            );
            receiverTokenAtas.push(receiverAta);
          } catch (ataError: any) {
            if (
              ataError?.message?.includes('OffCurve') ||
              ataError?.name === 'TokenOwnerOffCurveError'
            ) {
              const [manualAta] = PublicKey.findProgramAddressSync(
                [
                  receiverPk.toBuffer(),
                  TOKEN_PROGRAM_ID.toBuffer(),
                  extraTokenMintPk.toBuffer(),
                ],
                ASSOCIATED_TOKEN_PROGRAM_ID,
              );
              receiverTokenAtas.push(manualAta);
            } else {
              throw ataError;
            }
          }
        }
      }

      // Create instruction with NFT accounts
      const instruction =
        await solanaProgramClient.createClaimManyByAdminInstruction(
          wallet,
          groupId,
          numReceivers,
          receiverPks,
          groupOptions,
          {
            ...(extraTokenMintPk &&
              vaultAuthority &&
              tokenVaultAta &&
              receiverTokenAtas
              ? {
                  extraTokenMint: extraTokenMintPk,
                  vaultAuthority,
                  tokenVaultAta,
                  receiverTokenAtas,
                }
              : {}),
            ticketIds,
            nftMints,
            nftMetadatas,
            nftMasterEditions,
            receiverNftTokenAccounts,
          },
        );

      // Create transaction and send
      const transaction = new Transaction().add(instruction);
      await this.prepareTransaction(transaction);

      let signature: string;
      if (typeof wallet.signAndSendTransaction === 'function') {
        const res = await wallet.signAndSendTransaction(transaction);
        signature = res.signature;
      } else {
        signature = await wallet.sendTransaction(transaction, this.connection);
      }

      await this.connection.confirmTransaction(signature, 'confirmed');
      const slot = await this.connection.getSlot();
      devLog('Solana claimManyByAdmin transaction successful:', signature);
      return { success: true, hash: signature, signature, slot };
    } catch (error) {
      console.error('Failed to claim many by admin:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async claimGlobalJackpot(): Promise<TransactionResult> {
    try {
      devLog('Solana claimGlobalJackpot called');

      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      // Fetch global state for validations
      const globalState = await solanaProgramClient.getGlobalState();
      if (!globalState) {
        throw new Error('Global state not found');
      }

      // Validate winning hash is set
      const winningHash = globalState?.winningHash || globalState?.winning_hash;
      const isWinningHashSet =
        winningHash &&
        !(
          winningHash instanceof Uint8Array &&
          winningHash.every(byte => byte === 0)
        ) &&
        !(Array.isArray(winningHash) && winningHash.every(byte => byte === 0));

      if (!isWinningHashSet) {
        throw new Error(
          'Winning hash has not been set. Cannot claim global jackpot.',
        );
      }

      // Validate total tickets > 0
      const totalTickets =
        globalState?.totalTickets?.toNumber?.() ||
        globalState?.total_tickets?.toNumber?.() ||
        0;
      if (totalTickets === 0) {
        throw new Error('No tickets in system. Cannot claim global jackpot.');
      }

      // Validate closing time has passed
      const closingTime =
        globalState?.closingTime?.toNumber?.() ||
        globalState?.closing_time?.toNumber?.() ||
        0;
      const currentTime = Math.floor(Date.now() / 1000);
      if (currentTime < closingTime) {
        throw new Error(
          `Closing time has not passed yet. Current: ${currentTime}, Closing: ${closingTime}`,
        );
      }

      // Validate global jackpot not already claimed
      const globalJackpotClaimed =
        globalState?.globalJackpotClaimed ||
        globalState?.global_jackpot_claimed ||
        false;
      if (globalJackpotClaimed) {
        throw new Error('Global jackpot has already been claimed.');
      }

      // Validate winning ticket > 0
      const winningTicket =
        globalState?.globalWinningTicket?.toNumber?.() ||
        globalState?.global_winning_ticket?.toNumber?.() ||
        0;
      if (winningTicket === 0) {
        throw new Error('No winning ticket. Cannot claim global jackpot.');
      }

      const [winningNftMint] = solanaPDA.getNFTMintPDA(winningTicket);
      const winningNftMintPk = winningNftMint;

      // Derive winner NFT token account
      const {
        getAssociatedTokenAddress,
        ASSOCIATED_TOKEN_PROGRAM_ID,
        TOKEN_PROGRAM_ID,
        createAssociatedTokenAccountInstruction,
      } = await import('@solana/spl-token');

      let winnerNftTokenAccount: PublicKey;
      try {
        winnerNftTokenAccount = await getAssociatedTokenAddress(
          winningNftMintPk,
          wallet.publicKey,
        );
      } catch (ataError: any) {
        if (
          ataError?.message?.includes('OffCurve') ||
          ataError?.name === 'TokenOwnerOffCurveError'
        ) {
          const [manualAta] = PublicKey.findProgramAddressSync(
            [
              wallet.publicKey.toBuffer(),
              TOKEN_PROGRAM_ID.toBuffer(),
              collectionMintPk.toBuffer(),
            ],
            ASSOCIATED_TOKEN_PROGRAM_ID,
          );
          winnerNftTokenAccount = manualAta;
        } else {
          throw ataError;
        }
      }

      // Get purchaser - buyer of the group containing the winning ticket
      // We need to find the purchaser by querying TicketMetadata events or from group state
      // For now, try to get purchaser from the winning ticket's group
      let purchaser = wallet.publicKey; // Default fallback

      try {
        // Try to find purchaser from group state
        // The winning ticket belongs to a group, and the group's owner is the purchaser
        // We need to find which group contains the winning ticket
        // This is a simplified approach - in production, you might want to query events

        // Get all groups and find the one containing the winning ticket
        // For now, we'll use a heuristic: try to find group by checking if winning ticket is in range
        // This is not perfect but works for most cases

        // Alternative: Query TicketMetadata events to find purchaser
        // This requires parsing events which is more complex

        // For now, use winner as purchaser (commission will go to winner)
        // This is acceptable if winner is also the buyer of the winning ticket's group
        purchaser = wallet.publicKey;

        devLog(
          '[claimGlobalJackpot] Using winner as purchaser (simplified). In production, query TicketMetadata events to find actual purchaser.',
        );
      } catch (purchaserError: any) {
        console.warn(
          '[claimGlobalJackpot] Could not determine purchaser, using winner:',
          purchaserError?.message || purchaserError,
        );
        purchaser = wallet.publicKey;
      }

      // Create winner NFT token account if missing
      const winnerNftTokenAccountInfo = await this.connection.getAccountInfo(
        winnerNftTokenAccount,
      );
      const instructions: TransactionInstruction[] = [];

      if (!winnerNftTokenAccountInfo) {
        const createAtaIx = createAssociatedTokenAccountInstruction(
          wallet.publicKey,
          winnerNftTokenAccount,
          wallet.publicKey,
          winningNftMintPk,
        );
        instructions.push(createAtaIx);
      }

      // Create claim instruction
      const claimIx =
        await solanaProgramClient.createClaimGlobalJackpotInstruction(wallet, {
          purchaser,
          winningNftMint: winningNftMintPk,
          winnerNftTokenAccount,
        });
      instructions.push(claimIx);

      // Create transaction
      const transaction = new Transaction().add(...instructions);
      await this.prepareTransaction(transaction);

      // Send transaction
      let signature: string;
      if (typeof wallet.signAndSendTransaction === 'function') {
        const res = await wallet.signAndSendTransaction(transaction);
        signature = res.signature;
      } else {
        signature = await wallet.sendTransaction(transaction, this.connection);
      }

      // Wait for confirmation
      await this.connection.confirmTransaction(signature, 'confirmed');

      // Get slot for block number
      const slot = await this.connection.getSlot();

      devLog('Solana claimGlobalJackpot transaction successful:', signature);

      return {
        success: true,
        hash: signature,
        signature: signature,
        slot: slot,
      };
    } catch (error) {
      console.error('Failed to claim global jackpot:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async claimFundBack(params: {
    group: GroupIdentifier;
    groupId: string;
  }): Promise<TransactionResult> {
    try {
      devLog('Solana claimFundBack called with params:', params);

      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      const groupParams = this.ensureAutoGroupIdentifier(params.group, {
        requireGroupId: true,
      });

      // Parse string groupId to number or bigint first
      const { parseGroupIdFromString, groupIdToNumber } = await import(
        '../solana/pda'
      );
      let groupId: number;
      try {
        const parsed = parseGroupIdFromString(params.groupId);
        groupId = groupIdToNumber(parsed);
      } catch (error: any) {
        throw new Error(
          `group_id ${params.groupId} is too large. Please update claimFundBack to support large numbers.`,
        );
      }
      const groupOptions = {
        blockhashHash: groupParams.blockhashHash,
        buyer: groupParams.buyerPublicKey,
      };

      // Verify wallet is the owner (buyer)
      if (!wallet.publicKey.equals(groupParams.buyerPublicKey)) {
        throw new Error(
          'Wallet must be the group owner (buyer) to claim fund back',
        );
      }

      // Fetch global state for validation
      const globalState = await solanaProgramClient.getGlobalState();
      if (!globalState) {
        throw new Error('Global state not found');
      }

      // Validate closing time has passed
      const closingTime =
        globalState?.closingTime?.toNumber?.() ||
        globalState?.closing_time?.toNumber?.() ||
        0;
      const currentTime = Math.floor(Date.now() / 1000);
      if (currentTime < closingTime) {
        throw new Error(
          `Closing time has not passed yet. Current: ${currentTime}, Closing: ${closingTime}`,
        );
      }

      // Fetch group state to check remaining tickets
      const groupState =
        await solanaProgramClient.getGroupStateByBuyerAndBlockhash(
          groupParams.buyerPublicKey,
          groupParams.blockhashHash,
        );

      // Calculate remaining tickets and funds
      const size = groupState?.size || groupState?.groupSize || 0;
      const claimed = groupState?.claimed || groupState?.groupClaimed || 0;
      const remainingTickets = size - claimed;
      const ticketPrice =
        globalState?.ticketPriceLamports?.toNumber?.() ||
        globalState?.ticket_price_lamports?.toNumber?.() ||
        0;
      const remainingFunds = remainingTickets * ticketPrice;

      if (remainingFunds <= 0) {
        throw new Error(
          'No remaining funds to claim. All tickets have been claimed or no tickets remaining.',
        );
      }

      // Create instruction
      const instruction =
        await solanaProgramClient.createClaimFundBackInstruction(
          wallet,
          groupId,
          groupOptions,
        );

      // Create transaction and send
      const transaction = new Transaction().add(instruction);
      await this.prepareTransaction(transaction);

      let signature: string;
      if (typeof wallet.signAndSendTransaction === 'function') {
        const res = await wallet.signAndSendTransaction(transaction);
        signature = res.signature;
      } else {
        signature = await wallet.sendTransaction(transaction, this.connection);
      }

      await this.connection.confirmTransaction(signature, 'confirmed');
      const slot = await this.connection.getSlot();
      devLog('Solana claimFundBack transaction successful:', signature);
      return { success: true, hash: signature, signature, slot };
    } catch (error) {
      console.error('Failed to claim fund back:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async mintNft(params: {
    ticketId: string;
    receiver: string;
    group: GroupIdentifier;
  }): Promise<TransactionResult> {
    try {
      devLog('Solana mintNft called with params:', params);

      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      const { transaction } = await this.buildMintNftTransaction(
        params,
        wallet,
      );

      // Send transaction
      let signature: string;
      if (typeof wallet.signAndSendTransaction === 'function') {
        const res = await wallet.signAndSendTransaction(transaction);
        signature = res.signature;
      } else if (typeof wallet.signTransaction === 'function') {
        const signedTx = await wallet.signTransaction(transaction);
        signature = await this.connection.sendRawTransaction(
          signedTx.serialize(),
          {
            skipPreflight: false,
            maxRetries: 0,
            preflightCommitment: 'confirmed',
          },
        );
      } else {
        signature = await wallet.sendTransaction(transaction, this.connection);
      }

      // Wait for confirmation
      await this.connection.confirmTransaction(signature, 'confirmed');
      const slot = await this.connection.getSlot();

      devLog('Solana mintNft transaction successful:', signature);

      return {
        success: true,
        hash: signature,
        signature: signature,
        slot,
      };
    } catch (error) {
      console.error('Failed to mint NFT:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  private async buildMintNftTransaction(
    params: {
      ticketId: string;
      receiver: string;
      group: GroupIdentifier;
    },
    wallet: any,
  ): Promise<{
    transaction: Transaction;
    receiverPk: PublicKey;
    ticketId: bigint;
    groupParams: any;
  }> {
    if (!wallet || !wallet.publicKey) {
      throw new Error('Wallet not connected');
    }

    const ticketId = BigInt(params.ticketId);
    const receiverPk = new PublicKey(params.receiver);

    const groupParams = this.ensureAutoGroupIdentifier(params.group, {
      requireGroupId: false,
    });
    const groupOwnerPk = await this.getGroupOwnerPublicKey({
      buyerPublicKey: groupParams.buyerPublicKey,
      blockhashHash: groupParams.blockhashHash,
    });
    await this.assertWhitelistAuthority(wallet.publicKey, groupOwnerPk);

    const instruction = await solanaProgramClient.createMintNftInstruction(
      wallet,
      {
        ticketId,
        receiver: receiverPk,
        groupOptions: {
          blockhashHash: groupParams.blockhashHash!,
          buyer: groupParams.buyerPublicKey!,
        },
      },
    );

    const transaction = new Transaction().add(instruction);
    await this.prepareTransaction(transaction);

    return { transaction, receiverPk, ticketId, groupParams };
  }

  async claimLocalJackpot(params: {
    group: GroupIdentifier;
    groupId: string;
  }): Promise<TransactionResult> {
    try {
      devLog('Solana claimLocalJackpot called with params:', params);

      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      const groupParams = this.ensureAutoGroupIdentifier(params.group, {
        requireGroupId: true,
      });

      // Parse string groupId to number or bigint first
      const { parseGroupIdFromString, groupIdToNumber } = await import(
        '../solana/pda'
      );
      let groupId: number;
      try {
        const parsed = parseGroupIdFromString(params.groupId);
        groupId = groupIdToNumber(parsed);
      } catch (error: any) {
        throw new Error(
          `group_id ${params.groupId} is too large. Please update claimLocalJackpot to support large numbers.`,
        );
      }
      const groupOptions = {
        blockhashHash: groupParams.blockhashHash,
        buyer: groupParams.buyerPublicKey,
      };

      // Fetch group state to get extra token mint and validate
      const groupState =
        await solanaProgramClient.getGroupStateByBuyerAndBlockhash(
          groupParams.buyerPublicKey,
          groupParams.blockhashHash,
        );

      // Validate group has extra token mint (required for local jackpot)
      const extraTokenMintStr =
        groupState?.extraTokenMint?.toString?.() ||
        groupState?.extra_token_mint?.toString?.() ||
        undefined;

      if (
        !extraTokenMintStr ||
        extraTokenMintStr === PublicKey.default.toString()
      ) {
        throw new Error(
          'Group does not have extra token mint. Local jackpot requires extra token.',
        );
      }

      const extraTokenMintPk = new PublicKey(extraTokenMintStr);

      // Validate local prize amount > 0
      const localPrizeAmount =
        groupState?.localPrizeAmount?.toNumber?.() ||
        groupState?.local_prize_amount?.toNumber?.() ||
        0;
      if (localPrizeAmount <= 0) {
        throw new Error('Local prize amount is 0. Cannot claim local jackpot.');
      }

      // Validate winning hash is set
      const globalState = await solanaProgramClient.getGlobalState();
      const winningHash = globalState?.winningHash || globalState?.winning_hash;
      const isWinningHashSet =
        winningHash &&
        !(
          winningHash instanceof Uint8Array &&
          winningHash.every(byte => byte === 0)
        ) &&
        !(Array.isArray(winningHash) && winningHash.every(byte => byte === 0));

      if (!isWinningHashSet) {
        throw new Error(
          'Winning hash has not been set. Cannot claim local jackpot.',
        );
      }

      // Validate closing time has passed
      const closingTime =
        globalState?.closingTime?.toNumber?.() ||
        globalState?.closing_time?.toNumber?.() ||
        0;
      const currentTime = Math.floor(Date.now() / 1000);
      if (currentTime < closingTime) {
        throw new Error(
          `Closing time has not passed yet. Current: ${currentTime}, Closing: ${closingTime}`,
        );
      }

      // Derive vault authority PDA và token vault ATA
      const [vaultAuthority] = solanaPDA.getExtraTokenVaultPDA(
        groupParams.buyerPublicKey,
        groupParams.blockhashHash,
        extraTokenMintPk,
      );

      const {
        getAssociatedTokenAddress,
        ASSOCIATED_TOKEN_PROGRAM_ID,
        TOKEN_PROGRAM_ID,
        createAssociatedTokenAccountInstruction,
      } = await import('@solana/spl-token');

      let tokenVaultAta: PublicKey;
      try {
        tokenVaultAta = await getAssociatedTokenAddress(
          extraTokenMintPk,
          vaultAuthority,
        );
      } catch (ataError: any) {
        if (
          ataError?.message?.includes('OffCurve') ||
          ataError?.name === 'TokenOwnerOffCurveError'
        ) {
          const [manualAta] = PublicKey.findProgramAddressSync(
            [
              vaultAuthority.toBuffer(),
              TOKEN_PROGRAM_ID.toBuffer(),
              extraTokenMintPk.toBuffer(),
            ],
            ASSOCIATED_TOKEN_PROGRAM_ID,
          );
          tokenVaultAta = manualAta;
        } else {
          throw ataError;
        }
      }

      // Derive winner token ATA (for receiving tokens)
      let winnerTokenAta: PublicKey;
      try {
        winnerTokenAta = await getAssociatedTokenAddress(
          extraTokenMintPk,
          wallet.publicKey,
        );
      } catch (ataError: any) {
        if (
          ataError?.message?.includes('OffCurve') ||
          ataError?.name === 'TokenOwnerOffCurveError'
        ) {
          const [manualAta] = PublicKey.findProgramAddressSync(
            [
              wallet.publicKey.toBuffer(),
              TOKEN_PROGRAM_ID.toBuffer(),
              extraTokenMintPk.toBuffer(),
            ],
            ASSOCIATED_TOKEN_PROGRAM_ID,
          );
          winnerTokenAta = manualAta;
        } else {
          throw ataError;
        }
      }

      const localWinningTicket = this.calculateLocalWinningTicket(
        globalState,
        groupState,
      );
      const [localWinningNftMint] = solanaPDA.getNFTMintPDA(localWinningTicket);
      const localWinningNftMintPk = localWinningNftMint;

      // Derive winner NFT token account
      let winnerNftTokenAccount: PublicKey;
      try {
        winnerNftTokenAccount = await getAssociatedTokenAddress(
          localWinningNftMintPk,
          wallet.publicKey,
        );
      } catch (ataError: any) {
        if (
          ataError?.message?.includes('OffCurve') ||
          ataError?.name === 'TokenOwnerOffCurveError'
        ) {
          const [manualAta] = PublicKey.findProgramAddressSync(
            [
              wallet.publicKey.toBuffer(),
              TOKEN_PROGRAM_ID.toBuffer(),
              localWinningNftMintPk.toBuffer(),
            ],
            ASSOCIATED_TOKEN_PROGRAM_ID,
          );
          winnerNftTokenAccount = manualAta;
        } else {
          throw ataError;
        }
      }

      // Create winner token ATA if missing
      const winnerTokenAtaInfo = await this.connection.getAccountInfo(
        winnerTokenAta,
      );
      const instructions: TransactionInstruction[] = [];

      if (!winnerTokenAtaInfo) {
        const createAtaIx = createAssociatedTokenAccountInstruction(
          wallet.publicKey,
          winnerTokenAta,
          wallet.publicKey,
          extraTokenMintPk,
        );
        instructions.push(createAtaIx);
      }

      const winnerNftTokenAccountInfo = await this.connection.getAccountInfo(
        winnerNftTokenAccount,
      );
      if (!winnerNftTokenAccountInfo) {
        const createWinnerNftAtaIx = createAssociatedTokenAccountInstruction(
          wallet.publicKey,
          winnerNftTokenAccount,
          wallet.publicKey,
          localWinningNftMintPk,
        );
        instructions.push(createWinnerNftAtaIx);
      }

      // Create claim instruction
      const claimIx =
        await solanaProgramClient.createClaimLocalJackpotInstruction(
          wallet,
          groupId,
          groupOptions,
          {
            extraTokenMint: extraTokenMintPk,
            vaultAuthority,
            tokenVaultAta,
            winnerTokenAta,
            winningNftMint: localWinningNftMintPk,
            winnerNftTokenAccount,
          },
        );
      instructions.push(claimIx);

      const transaction = new Transaction().add(...instructions);
      await this.prepareTransaction(transaction);

      let signature: string;
      if (typeof wallet.signAndSendTransaction === 'function') {
        const res = await wallet.signAndSendTransaction(transaction);
        signature = res.signature;
      } else {
        signature = await wallet.sendTransaction(transaction, this.connection);
      }
      await this.connection.confirmTransaction(signature, 'confirmed');

      const slot = await this.connection.getSlot();

      return {
        success: true,
        hash: signature,
        signature: signature,
        slot,
      };
    } catch (error) {
      console.error('Failed to claim local jackpot:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async addWhitelister(params: {
    group: GroupIdentifier;
    user: string;
  }): Promise<TransactionResult> {
    try {
      devLog('Solana addWhitelister called with params:', params);

      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }
      const groupParams = this.ensureAutoGroupIdentifier(params.group, {
        requireGroupId: true,
      });

      // Parse string groupId to number or bigint first
      const { parseGroupIdFromString, groupIdToNumber, groupIdToBN } =
        await import('../solana/pda');
      let groupId: number;
      try {
        const parsed = parseGroupIdFromString(groupParams.groupId as string);
        groupId = groupIdToNumber(parsed);
      } catch (error: any) {
        // If group_id is too large, use BigInt and convert to number (may lose precision)
        const parsed = parseGroupIdFromString(groupParams.groupId as string);
        const groupIdBN = groupIdToBN(parsed);
        groupId = Number(groupIdBN.toString());
        console.warn(
          '[addWhitelister] group_id exceeds safe integer range, using BigInt:',
          groupParams.groupId,
        );
      }
      const userPk = new PublicKey(params.user);

      const options = {
        blockhashHash: groupParams.blockhashHash,
        buyer: groupParams.buyerPublicKey,
      };
      devLog('[addWhitelister] Using buy_tickets_auto PDA pattern:', {
        blockhashHash: groupParams.blockhashHash,
        buyer: groupParams.buyerAddress,
        groupId,
      });

      // Preflight: Verify group state account exists at the derived PDA
      const { solanaPDA } = await import('../solana/pda');
      const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
        groupParams.buyerPublicKey,
        groupParams.blockhashHash,
      );

      console.log(
        '[addWhitelister] Derived group state PDA:',
        groupStatePDA.toString(),
      );

      const groupStateAccount = await this.connection.getAccountInfo(
        groupStatePDA,
      );
      if (!groupStateAccount || !groupStateAccount.data) {
        throw new Error(
          `Group state account không tồn tại tại PDA: ${groupStatePDA.toString()}. ` +
            `Vui lòng đảm bảo group đã được tạo với buy_tickets_auto trước khi thêm whitelister. ` +
            `BlockhashHash: ${groupParams.blockhashHash}, Buyer: ${groupParams.buyerAddress}`,
        );
      }

      console.log('[addWhitelister] Group state account exists:', {
        dataLength: groupStateAccount.data.length,
        owner: groupStateAccount.owner.toString(),
        executable: groupStateAccount.executable,
        lamports: groupStateAccount.lamports,
      });

      // Try to decode account to verify it's valid (optional check)
      // Note: Decode failure here doesn't mean account is corrupt - IDL might not match exactly
      // Anchor program will handle deserialization during instruction execution
      let decodedGroupOwner: PublicKey | undefined;
      try {
        const { BorshAccountsCoder } = await import('@coral-xyz/anchor');
        const idl = await import('../../idl/lixinft_solana.json');
        if (idl && idl.default) {
          const coder = new BorshAccountsCoder(idl.default as any);
          // Try both account name variants
          let decoded: any = null;
          for (const accountName of ['GroupState', 'groupState']) {
            try {
              decoded = coder.decode(accountName, groupStateAccount.data);
              if (decoded) break;
            } catch (_) {
              continue;
            }
          }

          if (decoded) {
            console.log('[addWhitelister] Successfully decoded group state:', {
              groupId:
                decoded.groupId?.toString() || decoded.group_id?.toString(),
              owner: decoded.owner?.toString(),
              size: decoded.size || decoded.groupSize,
            });
          } else {
            console.warn(
              '[addWhitelister] Could not decode with BorshAccountsCoder, but account exists - Anchor will handle deserialization',
            );
          }
        }
      } catch (decodeError: any) {
        // Don't throw - this is just a preflight check
        // Anchor program will handle deserialization during instruction execution
        // IDL mismatch or version differences can cause decode to fail, but account is still valid
        console.warn(
          '[addWhitelister] Preflight decode check failed (non-blocking):',
          decodeError?.message || decodeError,
        );
        console.log(
          '[addWhitelister] Account exists and will be deserialized by Anchor program during instruction execution',
        );
      }

      const instruction =
        await solanaProgramClient.createAddWhitelisterInstruction(
          wallet,
          groupId,
          userPk,
          options,
        );

      const transaction = new Transaction().add(instruction);
      await this.prepareTransaction(transaction);

      // Debug: Log transaction details
      console.log('[SolanaAdapter.addWhitelister] Transaction details:', {
        instructions: transaction.instructions.length,
        recentBlockhash: transaction.recentBlockhash,
        feePayer: transaction.feePayer?.toString(),
        signatures: transaction.signatures.map(s => ({
          publicKey: s.publicKey.toString(),
          signature: s.signature
            ? s.signature instanceof Buffer
              ? s.signature.toString('hex')
              : Buffer.from(s.signature).toString('hex')
            : null,
        })),
      });
      const instructionKeysDetails = instruction.keys.map((k, idx) => ({
        index: idx,
        pubkey: k.pubkey.toString(),
        isSigner: k.isSigner,
        isWritable: k.isWritable,
      }));
      console.log('[SolanaAdapter.addWhitelister] Instruction details:', {
        programId: instruction.programId.toString(),
        keys: instructionKeysDetails,
        keysCount: instruction.keys.length,
        dataLength: instruction.data.length,
        dataHex:
          instruction.data instanceof Buffer
            ? instruction.data.toString('hex')
            : Buffer.from(instruction.data).toString('hex'),
      });
      console.log(
        '[SolanaAdapter.addWhitelister] Instruction keys JSON:',
        JSON.stringify(instructionKeysDetails, null, 2),
      );

      // Use signAndSendTransaction if available (Phantom API), otherwise use sendTransaction
      let signature: string;
      if (typeof wallet.signAndSendTransaction === 'function') {
        console.log(
          '[SolanaAdapter.addWhitelister] Using signAndSendTransaction',
        );
        try {
          // Serialize transaction to verify it's valid
          const serialized = transaction.serialize({
            requireAllSignatures: false,
            verifySignatures: false,
          });
          console.log(
            '[SolanaAdapter.addWhitelister] Transaction serialized successfully, length:',
            serialized.length,
          );

          // Simulate transaction to check for errors before sending
          // Note: simulateTransaction for legacy Transaction uses different API
          try {
            const simulation = await this.connection.simulateTransaction(
              transaction,
            );
            console.log(
              '[SolanaAdapter.addWhitelister] Transaction simulation result:',
              {
                err: simulation.value.err,
                logs: simulation.value.logs,
                unitsConsumed: simulation.value.unitsConsumed,
              },
            );
            if (simulation.value.err) {
              console.error(
                '[SolanaAdapter.addWhitelister] Transaction simulation failed:',
                simulation.value.err,
              );
              console.error(
                '[SolanaAdapter.addWhitelister] Simulation logs:',
                simulation.value.logs,
              );
              // Log full error details
              const errorDetails = JSON.stringify(
                simulation.value.err,
                null,
                2,
              );
              console.error(
                '[SolanaAdapter.addWhitelister] Full error details:',
                errorDetails,
              );
              throw new Error(`Transaction simulation failed: ${errorDetails}`);
            }
          } catch (simError: any) {
            console.error(
              '[SolanaAdapter.addWhitelister] Transaction simulation error:',
              simError?.message || simError,
            );
            // Continue anyway - simulation might fail but transaction could still work
          }

          const result = await wallet.signAndSendTransaction(transaction);
          signature = result.signature;
          console.log(
            '[SolanaAdapter.addWhitelister] Transaction sent successfully, signature:',
            signature,
          );
        } catch (txError: any) {
          console.error(
            '[SolanaAdapter.addWhitelister] signAndSendTransaction error:',
            {
              message: txError?.message,
              code: txError?.code,
              name: txError?.name,
              stack: txError?.stack,
              error: txError,
            },
          );

          // Fallback: Try sendTransaction if signAndSendTransaction fails
          if (typeof wallet.sendTransaction === 'function') {
            console.warn(
              '[SolanaAdapter.addWhitelister] signAndSendTransaction failed, trying sendTransaction as fallback',
            );
            try {
              signature = await wallet.sendTransaction(
                transaction,
                this.connection,
              );
              console.log(
                '[SolanaAdapter.addWhitelister] Transaction sent successfully via sendTransaction, signature:',
                signature,
              );
            } catch (sendTxError: any) {
              console.error(
                '[SolanaAdapter.addWhitelister] sendTransaction also failed:',
                {
                  message: sendTxError?.message,
                  code: sendTxError?.code,
                  name: sendTxError?.name,
                },
              );
              throw new Error(
                `Both signAndSendTransaction and sendTransaction failed. ` +
                  `signAndSendTransaction: ${
                    txError?.message || 'Unexpected error'
                  }, ` +
                  `sendTransaction: ${
                    sendTxError?.message || 'Unexpected error'
                  }`,
              );
            }
          } else {
            // No fallback available
            if (txError?.message) {
              throw new Error(`Phantom wallet error: ${txError.message}`);
            }
            throw txError;
          }
        }
      } else if (typeof wallet.sendTransaction === 'function') {
        console.log('[SolanaAdapter.addWhitelister] Using sendTransaction');
        signature = await wallet.sendTransaction(transaction, this.connection);
      } else {
        throw new Error(
          'Wallet does not support transaction sending. ' +
            'Please ensure Phantom wallet is properly installed and connected.',
        );
      }

      await this.connection.confirmTransaction(signature, 'confirmed');

      const slot = await this.connection.getSlot();
      return { success: true, hash: signature, signature, slot };
    } catch (error) {
      console.error('Failed to add whitelister:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Initialize or fix a group state account
   * This can handle both new accounts (init) and existing accounts (fix corrupted data)
   * Admin only
   */
  async initOrFixGroupState(params: {
    groupId: string;
  }): Promise<TransactionResult> {
    try {
      devLog('Solana initOrFixGroupState called with params:', params);

      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      // Parse string groupId to number or bigint first
      const { parseGroupIdFromString, groupIdToNumber, groupIdToBN } =
        await import('../solana/pda');
      let groupId: number;
      try {
        const parsed = parseGroupIdFromString(params.groupId);
        groupId = groupIdToNumber(parsed);
      } catch (error: any) {
        // If group_id is too large, use BigInt and convert to number (may lose precision)
        const parsed = parseGroupIdFromString(params.groupId);
        const groupIdBN = groupIdToBN(parsed);
        groupId = Number(groupIdBN.toString());
        console.warn(
          '[buyTickets] group_id exceeds safe integer range, using BigInt:',
          params.groupId,
        );
      }
      const instruction =
        await solanaProgramClient.createInitOrFixGroupStateInstruction(
          wallet,
          groupId,
        );

      const transaction = new Transaction().add(instruction);
      await this.prepareTransaction(transaction);

      // Use signAndSendTransaction if available (Phantom API), otherwise use sendTransaction
      let signature: string;
      if (typeof wallet.signAndSendTransaction === 'function') {
        devLog(
          '[SolanaAdapter.initOrFixGroupState] Using signAndSendTransaction',
        );
        try {
          const result = await wallet.signAndSendTransaction(transaction);
          signature = result.signature;
          devLog(
            '[SolanaAdapter.initOrFixGroupState] Transaction sent successfully, signature:',
            signature,
          );
        } catch (txError: any) {
          devLog(
            '[SolanaAdapter.initOrFixGroupState] signAndSendTransaction error:',
            txError,
          );
          if (typeof wallet.sendTransaction === 'function') {
            devLog(
              '[SolanaAdapter.initOrFixGroupState] Trying sendTransaction as fallback',
            );
            signature = await wallet.sendTransaction(
              transaction,
              this.connection,
            );
            const ownerField = decoded.owner;
            if (ownerField) {
              decodedGroupOwner =
                ownerField instanceof PublicKey
                  ? ownerField
                  : new PublicKey(ownerField.toString());
            }
          } else {
            throw new Error(
              `Phantom wallet error: ${txError?.message || 'Unexpected error'}`,
            );
          }
        }
      } else if (typeof wallet.sendTransaction === 'function') {
        devLog('[SolanaAdapter.initOrFixGroupState] Using sendTransaction');
        signature = await wallet.sendTransaction(transaction, this.connection);
      } else {
        throw new Error(
          'Wallet does not support transaction sending. ' +
            'Please ensure Phantom wallet is properly installed and connected.',
        );
      }

      if (!decodedGroupOwner) {
        decodedGroupOwner = await this.getGroupOwnerPublicKey({
          buyerPublicKey: groupParams.buyerPublicKey,
          blockhashHash: groupParams.blockhashHash,
        }) ?? undefined;
      }

      await this.assertWhitelistAuthority(wallet.publicKey, decodedGroupOwner);

      await this.connection.confirmTransaction(signature, 'confirmed');

      const slot = await this.connection.getSlot();
      return { success: true, hash: signature, signature, slot };
    } catch (error) {
      console.error('Failed to init or fix group state:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async removeWhitelister(params: {
    group: GroupIdentifier;
    user: string;
  }): Promise<TransactionResult> {
    try {
      devLog('Solana removeWhitelister called with params:', params);

      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      const groupParams = this.ensureAutoGroupIdentifier(params.group, {
        requireGroupId: true,
      });

      const groupOwnerPk = await this.getGroupOwnerPublicKey({
        buyerPublicKey: groupParams.buyerPublicKey,
        blockhashHash: groupParams.blockhashHash,
      });
      await this.assertWhitelistAuthority(wallet.publicKey, groupOwnerPk);

      // Parse string groupId to number or bigint first
      const { parseGroupIdFromString, groupIdToNumber, groupIdToBN } =
        await import('../solana/pda');
      let groupId: number;
      try {
        const parsed = parseGroupIdFromString(groupParams.groupId as string);
        groupId = groupIdToNumber(parsed);
      } catch (error: any) {
        // If group_id is too large, use BigInt and convert to number (may lose precision)
        const parsed = parseGroupIdFromString(groupParams.groupId as string);
        const groupIdBN = groupIdToBN(parsed);
        groupId = Number(groupIdBN.toString());
        console.warn(
          '[removeWhitelister] group_id exceeds safe integer range, using BigInt:',
          groupParams.groupId,
        );
      }
      const userPk = new PublicKey(params.user);
      const instruction =
        await solanaProgramClient.createRemoveWhitelisterInstruction(
          wallet,
          groupId,
          userPk,
          {
            blockhashHash: groupParams.blockhashHash,
            buyer: groupParams.buyerPublicKey,
          },
        );

      const transaction = new Transaction().add(instruction);
      await this.prepareTransaction(transaction);
      const signature = await wallet.sendTransaction(
        transaction,
        this.connection,
      );
      await this.connection.confirmTransaction(signature, 'confirmed');

      const slot = await this.connection.getSlot();
      return { success: true, hash: signature, signature, slot };
    } catch (error) {
      console.error('Failed to remove whitelister:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async addManyWhitelisters(params: {
    group: GroupIdentifier;
    users: string[];
  }): Promise<TransactionResult> {
    try {
      devLog('Solana addManyWhitelisters called with params:', params);

      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      const groupParams = this.ensureAutoGroupIdentifier(params.group, {
        requireGroupId: true,
      });

      const groupOwnerPk = await this.getGroupOwnerPublicKey({
        buyerPublicKey: groupParams.buyerPublicKey,
        blockhashHash: groupParams.blockhashHash,
      });
      await this.assertWhitelistAuthority(wallet.publicKey, groupOwnerPk);

      // Parse string groupId to number or bigint first
      const { parseGroupIdFromString, groupIdToNumber, groupIdToBN } =
        await import('../solana/pda');
      let groupId: number;
      try {
        const parsed = parseGroupIdFromString(groupParams.groupId as string);
        groupId = groupIdToNumber(parsed);
      } catch (error: any) {
        // If group_id is too large, use BigInt and convert to number (may lose precision)
        const parsed = parseGroupIdFromString(groupParams.groupId as string);
        const groupIdBN = groupIdToBN(parsed);
        groupId = Number(groupIdBN.toString());
        console.warn(
          '[addManyWhitelisters] group_id exceeds safe integer range, using BigInt:',
          groupParams.groupId,
        );
      }
      const userPks = params.users.map(u => new PublicKey(u));
      const instruction =
        await solanaProgramClient.createAddManyWhitelistersInstruction(
          wallet,
          groupId,
          userPks,
          {
            blockhashHash: groupParams.blockhashHash,
            buyer: groupParams.buyerPublicKey,
          },
        );

      const transaction = new Transaction().add(instruction);
      await this.prepareTransaction(transaction);
      const signature = await wallet.sendTransaction(
        transaction,
        this.connection,
      );
      await this.connection.confirmTransaction(signature, 'confirmed');

      const slot = await this.connection.getSlot();
      return { success: true, hash: signature, signature, slot };
    } catch (error) {
      console.error('Failed to add many whitelisters:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async removeManyWhitelisters(params: {
    group: GroupIdentifier;
    users: string[];
  }): Promise<TransactionResult> {
    try {
      devLog('Solana removeManyWhitelisters called with params:', params);

      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      const groupParams = this.ensureAutoGroupIdentifier(params.group, {
        requireGroupId: true,
      });

      const groupOwnerPk = await this.getGroupOwnerPublicKey({
        buyerPublicKey: groupParams.buyerPublicKey,
        blockhashHash: groupParams.blockhashHash,
      });
      await this.assertWhitelistAuthority(wallet.publicKey, groupOwnerPk);

      // Parse string groupId to number or bigint first
      const { parseGroupIdFromString, groupIdToNumber, groupIdToBN } =
        await import('../solana/pda');
      let groupId: number;
      try {
        const parsed = parseGroupIdFromString(groupParams.groupId as string);
        groupId = groupIdToNumber(parsed);
      } catch (error: any) {
        // If group_id is too large, use BigInt and convert to number (may lose precision)
        const parsed = parseGroupIdFromString(groupParams.groupId as string);
        const groupIdBN = groupIdToBN(parsed);
        groupId = Number(groupIdBN.toString());
        console.warn(
          '[removeManyWhitelisters] group_id exceeds safe integer range, using BigInt:',
          groupParams.groupId,
        );
      }

      // Validate batch size (max 50 per Rust code)
      if (params.users.length > 50) {
        throw new Error('Cannot remove more than 50 whitelisters at once');
      }

      const userPks = params.users.map(u => new PublicKey(u));
      const instruction =
        await solanaProgramClient.createRemoveManyWhitelistersInstruction(
          wallet,
          groupId,
          userPks,
          {
            blockhashHash: groupParams.blockhashHash,
            buyer: groupParams.buyerPublicKey,
          },
        );

      const transaction = new Transaction().add(instruction);
      await this.prepareTransaction(transaction);

      let signature: string;
      if (typeof wallet.signAndSendTransaction === 'function') {
        const res = await wallet.signAndSendTransaction(transaction);
        signature = res.signature;
      } else {
        signature = await wallet.sendTransaction(transaction, this.connection);
      }

      await this.connection.confirmTransaction(signature, 'confirmed');

      const slot = await this.connection.getSlot();
      return { success: true, hash: signature, signature, slot };
    } catch (error) {
      console.error('Failed to remove many whitelisters:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async setTicketPrice(params: {
    group: GroupIdentifier;
    price: string;
  }): Promise<TransactionResult> {
    try {
      devLog('Solana setTicketPrice called with params:', params);
      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) throw new Error('Wallet not connected');
      const lamports = Math.floor(parseFloat(params.price) * LAMPORTS_PER_SOL);
      const ix = await solanaProgramClient.createSetTicketPriceInstruction(
        wallet,
        lamports,
      );
      const tx = new Transaction().add(ix);
      await this.prepareTransaction(tx);
      const sig = await wallet.sendTransaction(tx, this.connection);
      await this.connection.confirmTransaction(sig, 'confirmed');
      const slot = await this.connection.getSlot();
      return { success: true, hash: sig, signature: sig, slot };
    } catch (error) {
      console.error('Failed to set ticket price:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async setClosingTime(params: {
    group: GroupIdentifier;
    closingTime: number;
  }): Promise<TransactionResult> {
    try {
      devLog('Solana setClosingTime called with params:', params);
      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) throw new Error('Wallet not connected');
      const ix = await solanaProgramClient.createSetClosingTimeInstruction(
        wallet,
        params.closingTime,
      );
      const tx = new Transaction().add(ix);
      await this.prepareTransaction(tx);
      const sig = await wallet.sendTransaction(tx, this.connection);
      await this.connection.confirmTransaction(sig, 'confirmed');
      const slot = await this.connection.getSlot();
      return { success: true, hash: sig, signature: sig, slot };
    } catch (error) {
      console.error('Failed to set closing time:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async setAdminStatus(params: {
    account: string;
    isAdmin: boolean;
  }): Promise<TransactionResult> {
    try {
      devLog('Solana setAdminStatus called with params:', params);
      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) throw new Error('Wallet not connected');
      const accountPk = new PublicKey(params.account);
      const ix = await solanaProgramClient.createUpdateAdminInstruction(
        wallet,
        accountPk,
        params.isAdmin,
      );
      const tx = new Transaction().add(ix);
      await this.prepareTransaction(tx);
      const sig = await wallet.sendTransaction(tx, this.connection);
      await this.connection.confirmTransaction(sig, 'confirmed');
      const slot = await this.connection.getSlot();
      return { success: true, hash: sig, signature: sig, slot };
    } catch (error) {
      console.error('Failed to set admin status:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async setJackpotConfig(params: {
    config: unknown;
  }): Promise<TransactionResult> {
    try {
      devLog('Solana setJackpotConfig called with params:', params);
      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) throw new Error('Wallet not connected');
      const cfg = params.config as any;
      const required = [
        'localJackpotPercentBps',
        'globalJackpotPercentBps',
        'globalJackpotCommissionBps',
        'globalJackpotServiceFeeBps',
      ];
      for (const k of required)
        if (typeof cfg?.[k] !== 'number')
          throw new Error(`Invalid config: missing ${k}`);
      const ix = await solanaProgramClient.createSetJackpotConfigInstruction(
        wallet,
        cfg,
      );
      const tx = new Transaction().add(ix);
      await this.prepareTransaction(tx);
      const sig = await wallet.sendTransaction(tx, this.connection);
      await this.connection.confirmTransaction(sig, 'confirmed');
      const slot = await this.connection.getSlot();
      return { success: true, hash: sig, signature: sig, slot };
    } catch (error) {
      console.error('Failed to set jackpot config:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async setWinningHash(params: {
    group: GroupIdentifier;
    hash: string;
  }): Promise<TransactionResult> {
    try {
      devLog('Solana setWinningHash called with params:', params);
      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) throw new Error('Wallet not connected');
      // Expect hex string or base64; convert to Uint8Array(32)
      let bytes: Uint8Array;
      try {
        const clean = params.hash.startsWith('0x')
          ? params.hash.slice(2)
          : params.hash;
        const arr = clean.match(/.{1,2}/g)?.map(b => parseInt(b, 16)) || [];
        bytes = new Uint8Array(arr);
      } catch {
        bytes = new Uint8Array(32);
      }
      const ix = await solanaProgramClient.createSetWinningHashInstruction(
        wallet,
        bytes,
      );
      const tx = new Transaction().add(ix);
      await this.prepareTransaction(tx);
      const sig = await wallet.sendTransaction(tx, this.connection);
      await this.connection.confirmTransaction(sig, 'confirmed');
      const slot = await this.connection.getSlot();
      return { success: true, hash: sig, signature: sig, slot };
    } catch (error) {
      console.error('Failed to set winning hash:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // View methods - Real implementation
  async getTicketInfo(ticketId: string): Promise<TicketInfo | null> {
    try {
      devLog('Fetching ticket info for ticketId:', ticketId);

      // Use program client to get ticket info
      const ticketInfo = await solanaProgramClient.getTicketInfo(
        parseInt(ticketId),
      );

      if (!ticketInfo) {
        return null;
      }

      // Convert to our interface format
      const result: TicketInfo = {
        id: ticketId,
        owner: ticketInfo.owner.toString(),
        groupId: ticketInfo.groupId.toString(),
        isClaimed: ticketInfo.isClaimed,
        claimTime: ticketInfo.claimTime,
        price: ticketInfo.price.toString(),
        currency: this.config.symbol,
      };

      devLog('Returning ticket info:', result);
      return result;
    } catch (error) {
      console.error('Failed to get ticket info:', error);
      return null;
    }
  }

  async getGroupInfo(group: GroupIdentifier): Promise<GroupInfo | null> {
    try {
      const groupParams = this.ensureAutoGroupIdentifier(group, {
        requireGroupId: false,
        requireBlockhash: false,
        requireBuyer: false,
      });
      devLog('Fetching group info for group:', groupParams);

      let groupState: any | null = null;
      let parsedGroupId: number | bigint | undefined = undefined;

      if (groupParams.buyerPublicKey && groupParams.blockhashHash) {
        try {
          groupState =
            await solanaProgramClient.getGroupStateByBuyerAndBlockhash(
              groupParams.buyerPublicKey,
              groupParams.blockhashHash,
            );
        } catch (error) {
          console.warn(
            '[getGroupInfo] Failed to fetch via buyer/blockhashHash, fallback to groupId if available:',
            error,
          );
        }
      }

      if (!groupState && groupParams.groupId) {
        const { parseGroupIdFromString } = await import('../solana/pda');
        parsedGroupId = parseGroupIdFromString(groupParams.groupId);
        try {
          groupState = await solanaProgramClient.getGroupState(parsedGroupId);
        } catch (fallbackError) {
          console.error(
            '[getGroupInfo] Fallback fetch by groupId failed:',
            fallbackError,
          );
          return null;
        }
      }

      if (!groupState) {
        return null;
      }

      // Convert to our interface format
      // Handle both snake_case and camelCase field names from Anchor
      const localPrizeAmount =
        groupState.localPrizeAmount ??
        groupState.local_prize_amount ??
        groupState.localJackpotPrize ??
        0;
      const extraTokenMint =
        groupState.extraTokenMint ??
        groupState.extra_token_mint ??
        groupState.localJackpotToken ??
        null;
      const localJackpotWinner =
        groupState.localJackpotWinner ??
        groupState.local_jackpot_winner ??
        null;
      const hash =
        groupState.hash ?? groupState.groupHash ?? new Uint8Array(16);
      const startTicketId =
        groupState.startTicketId ??
        groupState.start_ticket_id ??
        groupState.groupStartTicketId ??
        0;
      const size = groupState.size ?? groupState.groupSize ?? 0;
      const claimed = groupState.claimed ?? groupState.groupClaimed ?? 0;
      const owner =
        groupState.owner ?? groupState.groupOwner ?? PublicKey.default;

      const groupIdValue =
        groupState.groupId ??
        groupState.group_id ??
        (parsedGroupId !== undefined
          ? typeof parsedGroupId === 'bigint'
            ? parsedGroupId.toString()
            : parsedGroupId.toString()
          : groupParams.groupId);
      const groupIdString =
        typeof groupIdValue === 'string'
          ? groupIdValue
          : groupIdValue?.toString?.();

      const result: GroupInfo = {
        id: groupIdString || groupParams.groupId || groupParams.blockhashHash,
        owner: owner.toString(),
        ticketPrice: (localPrizeAmount / LAMPORTS_PER_SOL).toString(), // Convert from lamports
        currency: this.config.symbol,
        closingTime:
          hash && hash.length > 0 ? Date.now() + 24 * 60 * 60 * 1000 : 0, // Mock closing time
        isClosed: claimed >= size,
        totalTickets: size,
        claimedTickets: claimed,
        jackpotAmount: (localPrizeAmount / LAMPORTS_PER_SOL).toString(),
        // BNB specific fields for compatibility
        groupSize: size,
        groupClaimed: claimed,
        groupHash:
          hash instanceof Uint8Array
            ? Array.from(hash)
                .map(b => b.toString(16).padStart(2, '0'))
                .join('')
            : hash?.toString() ?? '',
        groupOwner: owner.toString(),
        groupLocalJackpotToken:
          extraTokenMint?.toString() ?? PublicKey.default.toString(),
        groupLocalJackpotTokenAmount:
          groupState.extraAmtPerTicket ??
          groupState.extra_amt_per_ticket ??
          groupState.localJackpotTokenAmount ??
          0,
        groupLocalJackpotPrize: localPrizeAmount,
        groupStartTicketId: startTicketId,
        groupLocalJackpotWinner:
          localJackpotWinner?.toString() ?? PublicKey.default.toString(),
      };

      devLog('Returning group info:', result);
      return result;
    } catch (error) {
      console.error('Failed to get group info:', error);
      return null;
    }
  }

  async getGlobalJackpotPrice(): Promise<JackpotInfo | null> {
    try {
      devLog('Fetching global jackpot price for Solana');

      // Get global state and jackpot vault balance
      const [globalState, jackpotBalance] = await Promise.all([
        solanaProgramClient.getGlobalState(),
        solanaProgramClient.getJackpotVaultBalance(),
      ]);

      if (!globalState) {
        return null;
      }

      // Convert from lamports to SOL
      const globalJackpotPrice =
        globalState.ticketPriceLamports / LAMPORTS_PER_SOL;
      const totalAmount = jackpotBalance / LAMPORTS_PER_SOL;

      const result: JackpotInfo = {
        globalJackpotPrice: globalJackpotPrice.toString(),
        currency: this.config.symbol,
        totalAmount: totalAmount.toString(),
      };

      devLog('Returning jackpot info:', result);
      return result;
    } catch (error) {
      console.error('Failed to get global jackpot price:', error);
      return null;
    }
  }

  async isWhitelisted(params: {
    group: GroupIdentifier;
    user: string;
  }): Promise<WhitelistInfo | null> {
    try {
      devLog('Checking whitelist status for Solana:', params);

      // Convert user string to PublicKey
      const userPubkey = new PublicKey(params.user);

      // Parse string groupId to number or bigint first
      const { parseGroupIdFromString, groupIdToNumber } = await import(
        '../solana/pda'
      );
      let groupIdNum: number;
      try {
        const groupParams = this.ensureAutoGroupIdentifier(params.group, {
          requireGroupId: true,
        });
        const parsed = parseGroupIdFromString(groupParams.groupId as string);
        groupIdNum = groupIdToNumber(parsed);
        const isWhitelisted = await solanaProgramClient.isWhitelisted(
          groupIdNum,
          userPubkey,
          {
            blockhashHash: groupParams.blockhashHash,
            buyer: groupParams.buyerPublicKey,
          },
        );

        const result: WhitelistInfo = {
          isWhitelisted,
          groupId: groupParams.groupId || groupParams.blockhashHash,
          user: params.user,
        };

        devLog('Returning whitelist info:', result);
        return result;
      } catch (error: any) {
        throw new Error(
          `group_id ${params.group?.groupId} is too large. Please update isWhitelisted to support large numbers.`,
        );
      }
    } catch (error) {
      console.error('Failed to check whitelist status:', error);
      return null;
    }
  }

  // SPL Token methods
  async getSPLTokenInfo(mintAddress: string): Promise<SPLTokenInfo | null> {
    try {
      const splToken = createSolanaSPLToken(this.connection);
      const mint = new PublicKey(mintAddress);
      return await splToken.getTokenInfo(mint);
    } catch (error) {
      console.error('Failed to get SPL token info:', error);
      return null;
    }
  }

  async getSPLTokenBalance(
    mintAddress: string,
    userAddress?: string,
  ): Promise<number> {
    try {
      // Validate mint address format first
      if (!mintAddress || typeof mintAddress !== 'string') {
        console.warn('Invalid mint address format:', mintAddress);
        return 0;
      }

      let mint: PublicKey;
      try {
        mint = new PublicKey(mintAddress);
      } catch (error) {
        console.warn(
          `Invalid PublicKey format for mint address: ${mintAddress}`,
          error,
        );
        return 0;
      }

      const splToken = createSolanaSPLToken(this.connection);
      const user = userAddress
        ? new PublicKey(userAddress)
        : this.ensureWallet()?.publicKey;

      if (!user) {
        console.warn('User address not provided for token balance check');
        return 0;
      }

      return await splToken.getTokenBalance(user, mint);
    } catch (error: any) {
      // Handle specific error cases
      if (
        error?.message?.includes('Invalid public key') ||
        error?.message?.includes('could not be unpacked') ||
        error?.message?.includes('Invalid param')
      ) {
        console.warn(
          `Failed to get token balance for ${mintAddress}:`,
          error.message,
        );
        return 0;
      }
      console.error('Failed to get SPL token balance:', error);
      return 0;
    }
  }

  async getCommonSPLTokens(): Promise<Record<string, SPLTokenInfo>> {
    try {
      const splToken = createSolanaSPLToken(this.connection);
      // Use devnet tokens for now, can be made configurable
      return splToken.getCommonSPLTokensDevnet();
    } catch (error) {
      console.error('Failed to get common SPL tokens:', error);
      return {};
    }
  }

  async approveSPLToken(
    mintAddress: string,
    amount: number,
  ): Promise<TransactionResult> {
    try {
      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      const splToken = createSolanaSPLToken(this.connection);
      const mint = new PublicKey(mintAddress);

      // Get token info for decimals
      const tokenInfo = await splToken.getTokenInfo(mint);
      if (!tokenInfo) {
        throw new Error('Token not found');
      }

      // Get associated token account
      const associatedTokenAccount = await splToken.getAssociatedTokenAddress(
        wallet.publicKey,
        mint,
      );

      // Create approve instruction
      const instruction = splToken.createApproveInstruction(
        associatedTokenAccount,
        new PublicKey(SOLANA_PROGRAM_ID), // Approve the program
        wallet.publicKey,
        amount,
        tokenInfo.decimals,
      );

      // Create transaction
      const transaction = new Transaction().add(instruction);
      await this.prepareTransaction(transaction);

      // Send transaction
      const signature = await wallet.sendTransaction(
        transaction,
        this.connection,
      );

      // Wait for confirmation
      await this.connection.confirmTransaction(signature, 'confirmed');

      return {
        success: true,
        hash: signature,
        signature: signature,
      };
    } catch (error) {
      console.error('Failed to approve SPL token:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async getTokenDecimals(tokenAddress: string): Promise<number> {
    // Native token (SOL) uses 9 decimals
    if (
      !tokenAddress ||
      tokenAddress === '0x0000000000000000000000000000000000000000' ||
      tokenAddress === ''
    ) {
      return 9; // SOL decimals
    }

    // Validate it's not an Ethereum address
    if (tokenAddress.startsWith('0x')) {
      throw new Error(
        'Invalid Solana address: Ethereum address format detected',
      );
    }

    try {
      const { getMint } = await import('@solana/spl-token');
      const mint = new (await import('@solana/web3.js')).PublicKey(
        tokenAddress,
      );
      const mintInfo = await getMint(this.connection, mint);
      return mintInfo.decimals;
    } catch (error) {
      console.error('Failed to get token decimals:', error);
      throw new Error(
        `Failed to get token decimals: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  // Unified approval check (Solana SPL token)
  async checkTokenApproval(params: {
    tokenAddress: string;
    spender: string;
    owner?: string;
    requiredAmountWei?: string;
  }): Promise<{ approved: boolean; allowanceWei: string }> {
    try {
      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      // Native token (SOL) doesn't need approval
      if (
        !params.tokenAddress ||
        params.tokenAddress === '0x0000000000000000000000000000000000000000' ||
        params.tokenAddress === ''
      ) {
        return { approved: true, allowanceWei: '0' };
      }

      // Validate addresses are not Ethereum format (0x...)
      if (params.tokenAddress.startsWith('0x')) {
        return { approved: false, allowanceWei: '0' };
      }
      if (!params.spender || params.spender.startsWith('0x')) {
        return { approved: false, allowanceWei: '0' };
      }
      if (params.owner && params.owner.startsWith('0x')) {
        return { approved: false, allowanceWei: '0' };
      }

      const { getAssociatedTokenAddress, getAccount } = await import(
        '@solana/spl-token'
      );

      // Validate addresses before creating PublicKey
      let owner: PublicKey;
      let spender: PublicKey;
      let mint: PublicKey;

      try {
        owner = params.owner ? new PublicKey(params.owner) : wallet.publicKey;
        spender = new PublicKey(params.spender);
        mint = new PublicKey(params.tokenAddress);
      } catch (error) {
        // Invalid address format - return silently to prevent console spam
        return { approved: false, allowanceWei: '0' };
      }

      // Get associated token account
      const tokenAccount = await getAssociatedTokenAddress(mint, owner);

      // Get account info
      let tokenAccountInfo;
      try {
        tokenAccountInfo = await getAccount(this.connection, tokenAccount);
      } catch (error) {
        // Token account doesn't exist or has no balance
        return { approved: false, allowanceWei: '0' };
      }

      // Check if delegate authority is set and matches spender
      const delegate = tokenAccountInfo.delegate;
      if (!delegate) {
        return { approved: false, allowanceWei: '0' };
      }

      // Check if delegate matches the spender
      if (delegate.toString() !== spender.toString()) {
        return { approved: false, allowanceWei: '0' };
      }

      // Get delegate amount
      const delegateAmount = tokenAccountInfo.delegatedAmount.toString();

      // Check if amount is sufficient if requiredAmountWei is provided
      if (params.requiredAmountWei) {
        const requiredAmount = BigInt(params.requiredAmountWei);
        const delegatedAmount = BigInt(delegateAmount);
        const approved = delegatedAmount >= requiredAmount;
        return { approved, allowanceWei: delegateAmount };
      }

      // If no required amount, just check if delegate exists
      return { approved: true, allowanceWei: delegateAmount };
    } catch (error) {
      console.error('Failed to check token approval:', error);
      return { approved: false, allowanceWei: '0' };
    }
  }

  // Unified approval method (Solana SPL token)
  async approveToken(params: {
    tokenAddress: string;
    spender: string;
    amountWei?: string;
  }): Promise<TransactionResult> {
    try {
      const wallet = this.ensureWallet();
      if (!wallet) {
        throw new Error('Wallet not connected');
      }

      // Ensure wallet is connected and has publicKey
      if (!wallet.publicKey) {
        // Try to connect if not connected
        try {
          const response = await wallet.connect();
          if (!response || !response.publicKey) {
            throw new Error('Failed to connect wallet');
          }
        } catch (connectError) {
          throw new Error(
            'Wallet not connected. Please connect your wallet first.',
          );
        }
      }

      // Validate wallet has sendTransaction method
      // Phantom wallet should have sendTransaction, but let's verify
      if (!wallet.sendTransaction && !wallet.signAndSendTransaction) {
        console.error('[approveToken] Wallet object:', wallet);
        console.error('[approveToken] Wallet type:', typeof wallet);
        console.error(
          '[approveToken] Wallet methods:',
          Object.keys(wallet || {}),
        );
        console.error(
          '[approveToken] Wallet prototype:',
          Object.getPrototypeOf(wallet || {}),
        );
        throw new Error(
          'Wallet does not support sendTransaction. Please ensure Phantom wallet is properly installed and connected.',
        );
      }

      // Use signAndSendTransaction if available (newer Phantom API), otherwise use sendTransaction
      const sendTxMethod =
        wallet.signAndSendTransaction || wallet.sendTransaction;

      // Native token (SOL) doesn't need approval
      if (
        !params.tokenAddress ||
        params.tokenAddress === '0x0000000000000000000000000000000000000000' ||
        params.tokenAddress === ''
      ) {
        return {
          success: true,
          hash: '',
          signature: '',
          slot: await this.connection.getSlot(),
        };
      }

      // Validate token address is a valid Solana address (not Ethereum 0x...)
      if (params.tokenAddress.startsWith('0x')) {
        throw new Error(
          'Invalid Solana address: Ethereum address format detected',
        );
      }

      const { getAssociatedTokenAddress, createApproveInstruction } =
        await import('@solana/spl-token');

      // Validate addresses before creating PublicKey
      let mint: PublicKey;
      let spender: PublicKey;
      try {
        mint = new PublicKey(params.tokenAddress);
        spender = new PublicKey(params.spender);
      } catch (error) {
        throw new Error(
          `Invalid Solana address format: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
        );
      }

      // Get associated token account
      const tokenAccount = await getAssociatedTokenAddress(
        mint,
        wallet.publicKey,
      );

      // Get token decimals
      const decimals = await this.getTokenDecimals(params.tokenAddress);

      // Convert amountWei to number (amountWei is in smallest unit, decimals already applied)
      // If amountWei is not provided, use max amount (all balance)
      let amount: bigint;
      if (params.amountWei) {
        amount = BigInt(params.amountWei);
      } else {
        // Get token balance to approve max
        const balance = await this.getSPLTokenBalance(
          params.tokenAddress,
          wallet.publicKey.toString(),
        );
        // Convert UI amount to raw amount
        amount = BigInt(Math.floor(balance * Math.pow(10, decimals)));
      }

      // Create approve instruction
      const instruction = createApproveInstruction(
        tokenAccount,
        spender,
        wallet.publicKey,
        amount,
      );

      // Create transaction
      const transaction = new Transaction().add(instruction);
      await this.prepareTransaction(transaction);

      console.log('[approveToken] Transaction prepared, sending via wallet...');
      console.log('[approveToken] Wallet type:', typeof wallet);
      console.log(
        '[approveToken] Wallet has signAndSendTransaction:',
        typeof wallet.signAndSendTransaction === 'function',
      );
      console.log(
        '[approveToken] Wallet has sendTransaction:',
        typeof wallet.sendTransaction === 'function',
      );

      // Send transaction - Phantom wallet uses signAndSendTransaction
      // This method signs and sends in one step, opening wallet popup for user confirmation
      let signature: string;

      if (typeof wallet.signAndSendTransaction === 'function') {
        // Phantom wallet API (recommended method)
        // signAndSendTransaction automatically signs and sends, opens popup for user approval
        console.log(
          '[approveToken] Using signAndSendTransaction (Phantom API)',
        );
        try {
          const result = await wallet.signAndSendTransaction(transaction);
          signature = result.signature;
        } catch (txError: any) {
          console.error(
            '[approveToken] signAndSendTransaction error:',
            txError,
          );
          // User rejected transaction or other error
          throw new Error(
            txError?.message ||
              'User rejected transaction or transaction failed',
          );
        }
      } else if (typeof wallet.sendTransaction === 'function') {
        // Fallback for older wallet implementations
        console.log('[approveToken] Using sendTransaction (fallback)');
        signature = await wallet.sendTransaction(transaction, this.connection);
      } else {
        console.error(
          '[approveToken] Wallet methods available:',
          Object.keys(wallet || {}),
        );
        throw new Error(
          'Wallet does not support transaction sending. ' +
            'Please ensure Phantom wallet is properly installed and connected.',
        );
      }

      console.log(
        '[approveToken] Transaction sent successfully, signature:',
        signature,
      );

      // Wait for confirmation
      await this.connection.confirmTransaction(signature, 'confirmed');

      const slot = await this.connection.getSlot();

      return {
        success: true,
        hash: signature,
        signature: signature,
        slot: slot,
      };
    } catch (error) {
      console.error('Failed to approve token:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Token approval event subscription (Solana - uses polling/account change)
  onTokenApproval?(
    tokenAddress: string,
    spender: string,
    callback: (data: {
      owner: string;
      spender: string;
      amountWei: string;
    }) => void,
  ): () => void {
    try {
      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        return () => void 0;
      }

      const owner = wallet.publicKey.toString();
      let lastApprovalState: {
        approved: boolean;
        allowanceWei: string;
      } | null = null;

      // Poll for approval changes
      const pollInterval = setInterval(async () => {
        try {
          const currentState = await this.checkTokenApproval({
            tokenAddress,
            spender,
            owner,
          });

          // Check if approval state changed
          if (
            !lastApprovalState ||
            lastApprovalState.approved !== currentState.approved ||
            lastApprovalState.allowanceWei !== currentState.allowanceWei
          ) {
            // State changed - check if it's now approved
            if (
              currentState.approved &&
              (!lastApprovalState || !lastApprovalState.approved)
            ) {
              // Approval happened
              callback({
                owner,
                spender,
                amountWei: currentState.allowanceWei,
              });
            }
            lastApprovalState = currentState;
          }
        } catch (error) {
          console.error('Error polling token approval:', error);
        }
      }, 2000); // Poll every 2 seconds

      // Also try to use account change listener for better performance
      let accountChangeListener: number | null = null;
      (async () => {
        try {
          const { getAssociatedTokenAddress } = await import(
            '@solana/spl-token'
          );
          const mint = new PublicKey(tokenAddress);
          const tokenAccount = await getAssociatedTokenAddress(
            mint,
            wallet.publicKey,
          );

          accountChangeListener = this.connection.onAccountChange(
            tokenAccount,
            async () => {
              try {
                const currentState = await this.checkTokenApproval({
                  tokenAddress,
                  spender,
                  owner,
                });

                if (currentState.approved) {
                  callback({
                    owner,
                    spender,
                    amountWei: currentState.allowanceWei,
                  });
                }
              } catch (error) {
                console.error(
                  'Error checking approval on account change:',
                  error,
                );
              }
            },
          );
        } catch (error) {
          // Account might not exist yet, polling will handle it
          console.error('Error setting up account change listener:', error);
        }
      })();

      // Return unsubscribe function
      return () => {
        clearInterval(pollInterval);
        if (accountChangeListener !== null) {
          try {
            this.connection.removeAccountChangeListener(accountChangeListener);
          } catch (error) {
            console.error('Error removing account change listener:', error);
          }
        }
      };
    } catch (error) {
      console.error('Error setting up token approval listener:', error);
      return () => void 0;
    }
  }

  async sendRewardToMany(receivers: string[], value: number): Promise<any> {
    const { SystemProgram, Transaction, PublicKey } = await import(
      '@solana/web3.js'
    );
    const wallet = this.ensureWallet();
    if (!wallet || !wallet.publicKey) throw new Error('Wallet not connected');
    const tx = new Transaction();
    const lamports = value > 0 ? value : 0.01 * 1e9;
    receivers.forEach(addr => {
      tx.add(
        SystemProgram.transfer({
          fromPubkey: wallet.publicKey,
          toPubkey: new PublicKey(addr),
          lamports,
        }),
      );
    });
    await this.prepareTransaction(tx);
    const signature = await wallet.sendTransaction(tx, this.connection);
    await this.connection.confirmTransaction(signature, 'confirmed');
    return { success: true, signature };
  }
  async createCampaign(params: {
    numMint: number;
    extraToken: string;
    extraTokenAmount: string;
    receivers: string[];
    value: number;
    lixiBagAddress: string;
  }): Promise<any> {
    try {
      devLog('Solana createCampaign called with params:', params);

      const wallet = this.ensureWallet();
      if (!wallet || !wallet.publicKey) {
        throw new Error('Wallet not connected');
      }

      const extraTokenMint =
        params.extraToken &&
        params.extraToken !== '0x0000000000000000000000000000000000000000'
          ? params.extraToken
          : undefined;

      // Read global_state to get next_group_id for auto flow
      // IMPORTANT: Verify global_state account exists and is valid before proceeding
      const { solanaPDA: solanaPDAInstance } = await import('../solana/pda');
      const [globalStatePDA] = solanaPDAInstance.getGlobalStatePDA();

      // Check if global_state account exists
      const globalStateAccountInfo = await this.connection.getAccountInfo(
        globalStatePDA,
      );
      if (
        !globalStateAccountInfo ||
        !globalStateAccountInfo.data ||
        globalStateAccountInfo.data.length < 8
      ) {
        throw new Error(
          'Global state account does not exist or is invalid. ' +
            'Please initialize the global state first by calling initialize_global instruction. ' +
            `Expected account: ${globalStatePDA.toString()}`,
        );
      }

      // Verify account is owned by the program
      const programId = solanaProgramClient.getProgramId();
      if (!globalStateAccountInfo.owner.equals(programId)) {
        throw new Error(
          `Global state account is not owned by the program. ` +
            `Owner: ${globalStateAccountInfo.owner.toString()}, ` +
            `Expected: ${programId.toString()}`,
        );
      }

      // Check account size - expected size is 227 bytes (8 discriminator + 219 data)
      // But old accounts might have 219 bytes total (8 + 211)
      const accountDataSize = globalStateAccountInfo.data.length;
      const expectedSize = 227; // Current struct size
      const oldSize = 219; // Old struct size (without next_vrf_request_id, vrf_authority, global_jackpot_claimed)

      if (accountDataSize !== expectedSize && accountDataSize !== oldSize) {
        console.warn(
          `[createCampaign] Global state account size mismatch: ` +
            `expected ${expectedSize} or ${oldSize} bytes, got ${accountDataSize} bytes. ` +
            `This may cause deserialization errors.`,
        );
      }

      if (accountDataSize === oldSize) {
        throw new Error(
          `Global state account was created with an old struct version (${oldSize} bytes). ` +
            `The current program expects ${expectedSize} bytes. ` +
            `Please re-initialize the global state by calling initialize_global instruction, ` +
            `or migrate the account data to the new format. ` +
            `Account: ${globalStatePDA.toString()}`,
        );
      }

      devLog('[createCampaign] Global state account verified:', {
        exists: true,
        address: globalStatePDA.toString(),
        dataLength: globalStateAccountInfo.data.length,
        expectedSize,
        oldSize,
        isCorrectSize: accountDataSize === expectedSize,
        lamports: globalStateAccountInfo.lamports,
        owner: globalStateAccountInfo.owner.toString(),
      });

      // Build buy_tickets_auto instruction
      // Note: group_id will be calculated automatically in createBuyTicketsAutoInstruction
      // from buyer + slot + timestamp (same as Rust implementation)
      // Note: params.extraTokenAmount is đã ở dạng raw (parseUnits)
      // Đây là amount PER ITEM, cần nhân với numMint để ra tổng (u64)
      let extraTokenAmountRaw = 0n;
      let extraTokenAmountString: string | undefined;
      if (extraTokenMint) {
        // params.extraTokenAmount is already in base units (from parseUnits)
        // It's amount per item, so multiply by numMint to get total
        const amountPerItemBaseUnits = BigInt(params.extraTokenAmount || '0');
        const totalAmountBaseUnits =
          amountPerItemBaseUnits * BigInt(params.numMint);
        extraTokenAmountRaw = totalAmountBaseUnits;
        extraTokenAmountString = totalAmountBaseUnits.toString();

        devLog('[createCampaign] Extra token amount calculation:', {
          amountPerItemBaseUnits: amountPerItemBaseUnits.toString(),
          numMint: params.numMint,
          totalAmountBaseUnits: totalAmountBaseUnits.toString(),
          extraTokenAmountRaw: extraTokenAmountString,
        });
      }
      // receivers is optional - can be empty array, receivers can be added later via claim or whitelist
      const receivers = (params.receivers || [])
        .filter(x => !!x)
        .map(addr => {
          try {
            return new PublicKey(addr);
          } catch (error) {
            console.error(
              '[createCampaign] Invalid receiver address:',
              addr,
              error,
            );
            throw new Error(`Invalid receiver address: ${addr}`);
          }
        });

      devLog('[createCampaign] Building instruction with:', {
        numTickets: params.numMint,
        extraTokenMint: extraTokenMint?.toString(),
        extraTokenAmount: extraTokenAmountString,
        receiversCount: receivers.length,
        note: 'group_state PDA derived from buyer + slot (pattern: [b"group", buyer, slot]) - no group_id needed for PDA',
      });

      // Check buyer balance - need SOL for:
      // 1. Group account rent (if not exists)
      // 2. Ticket purchase cost
      // 3. Transaction fees
      try {
        const buyerBalance = await this.connection.getBalance(wallet.publicKey);
        const buyerBalanceSOL = buyerBalance / LAMPORTS_PER_SOL;

        // Calculate estimated costs
        // Group account rent: ~0.0015 SOL (based on Rust code space calculation)
        // Ticket price: from global state
        const globalState = await solanaProgramClient.getGlobalState();
        const ticketPriceLamports =
          globalState?.ticketPriceLamports ||
          globalState?.ticket_price_lamports ||
          0;
        const ticketPriceSOL = ticketPriceLamports / LAMPORTS_PER_SOL;
        const totalTicketsCost = ticketPriceSOL * params.numMint;

        // Estimate transaction fee: ~0.000005 SOL
        const estimatedFee = 0.00001;
        const estimatedGroupRent = 0.002; // Safe estimate
        const estimatedTotal =
          totalTicketsCost + estimatedFee + estimatedGroupRent;

        devLog('[createCampaign] Balance check:', {
          buyerBalance: buyerBalanceSOL,
          ticketPricePerTicket: ticketPriceSOL,
          totalTicketsCost,
          estimatedFee,
          estimatedGroupRent,
          estimatedTotal,
          hasEnoughFunds: buyerBalanceSOL >= estimatedTotal,
        });

        if (buyerBalanceSOL < estimatedTotal) {
          throw new Error(
            `Insufficient SOL balance. Need approximately ${estimatedTotal.toFixed(
              4,
            )} SOL ` +
              `(tickets: ${totalTicketsCost.toFixed(
                4,
              )}, rent: ${estimatedGroupRent.toFixed(
                4,
              )}, fee: ${estimatedFee.toFixed(4)}), ` +
              `but have ${buyerBalanceSOL.toFixed(4)} SOL`,
          );
        }
      } catch (balanceError: any) {
        if (balanceError?.message?.includes('Insufficient SOL')) {
          throw balanceError;
        }
        console.warn('[createCampaign] Could not check balance:', balanceError);
      }

      // Get blockhash_hash before creating instruction (needed to read group_id after transaction)
      // This ensures we use the same blockhash_hash that will be used in the transaction
      const { blockhash: recentBlockhash } =
        await this.connection.getLatestBlockhash('confirmed');
      const { sha256 } = await import('@noble/hashes/sha256');
      const { decode } = await import('bs58');
      const blockhashBytes = decode(recentBlockhash);
      const blockhashHash = sha256(blockhashBytes);
      const blockhashHashBuffer = Buffer.from(blockhashHash);
      // Use DataView for cross-platform compatibility
      const blockhashHashDataView = new DataView(
        blockhashHashBuffer.buffer,
        blockhashHashBuffer.byteOffset,
        blockhashHashBuffer.byteLength,
      );
      const blockhashHashU32 = blockhashHashDataView.getUint32(0, true); // true = little endian, u32 (4 bytes)
      // Keep as string to avoid precision loss
      const blockhashHashString = blockhashHashU32.toString();
      console.log('blockhashHashString', blockhashHashString);

      let ix: TransactionInstruction;
      let transferExtraIx: TransactionInstruction | null = null;
      let derivedBuyerTokenAta: PublicKey | undefined;
      let derivedVaultAuthority: PublicKey | undefined;
      let derivedGroupVaultAta: PublicKey | undefined;
      const ataSetupInstructions: TransactionInstruction[] = [];

      try {
        ix = await solanaProgramClient.createBuyTicketsAutoInstruction(wallet, {
          numTickets: params.numMint,
          extraTokenMint: extraTokenMint
            ? new PublicKey(extraTokenMint)
            : undefined,
          extraTokenAmount: extraTokenAmountString,
          receivers: receivers.length > 0 ? receivers : undefined, // Optional - can be empty
          blockhashHash: blockhashHashString, // Pass blockhash_hash as string to avoid precision loss
        });
        devLog('[createCampaign] Instruction created successfully');

        if (extraTokenMint) {
          const extraMintPk = new PublicKey(extraTokenMint);
          const {
            getAssociatedTokenAddress,
            createAssociatedTokenAccountInstruction,
          } = await import('@solana/spl-token');
          derivedBuyerTokenAta = await getAssociatedTokenAddress(
            extraMintPk,
            wallet.publicKey,
          );
          [derivedVaultAuthority] = solanaPDA.getExtraTokenVaultPDA(
            wallet.publicKey,
            blockhashHashString,
            extraMintPk,
          );
          derivedGroupVaultAta = await getAssociatedTokenAddress(
            extraMintPk,
            derivedVaultAuthority,
            true,
          );

          // Ensure buyer ATA exists
          const buyerAtaInfo = await this.connection.getAccountInfo(
            derivedBuyerTokenAta,
          );
          if (!buyerAtaInfo) {
            ataSetupInstructions.push(
              createAssociatedTokenAccountInstruction(
                wallet.publicKey,
                derivedBuyerTokenAta,
                wallet.publicKey,
                extraMintPk,
              ),
            );
          }

          // Ensure vault ATA exists (owner is PDA, allow off curve)
          const vaultAtaInfo = await this.connection.getAccountInfo(
            derivedGroupVaultAta,
          );
          if (!vaultAtaInfo) {
            ataSetupInstructions.push(
              createAssociatedTokenAccountInstruction(
                wallet.publicKey,
                derivedGroupVaultAta,
                derivedVaultAuthority,
                extraMintPk,
              ),
            );
          }

          transferExtraIx =
            await solanaProgramClient.createTransferExtraTokensInstruction(
              wallet,
              {
                blockhashHash: blockhashHashString,
                extraTokenMint: extraMintPk,
                buyerTokenAta: derivedBuyerTokenAta,
                vaultAuthority: derivedVaultAuthority,
                vaultAta: derivedGroupVaultAta,
              },
            );
          devLog('[createCampaign] Added transferExtraTokens instruction');
        }
      } catch (instructionError: any) {
        console.error(
          '[createCampaign] Failed to create instruction:',
          instructionError,
        );
        throw new Error(
          `Failed to create instruction: ${
            instructionError?.message ||
            instructionError?.toString() ||
            'Unknown error'
          }`,
        );
      }

      const tx = new Transaction();
      const postCreationTransactions: {
        label: 'deposit_group_spl' | 'mint_nft';
        transaction: Transaction;
        context?: Record<string, any>;
      }[] = [];
      ataSetupInstructions.forEach(setupIx => tx.add(setupIx));
      tx.add(ix);
      if (transferExtraIx) {
        tx.add(transferExtraIx);
      }

      try {
        await this.prepareTransaction(tx);
        devLog('[createCampaign] Transaction prepared successfully');

        // Log transaction details for debugging
        if (process.env.NODE_ENV !== 'production') {
          try {
            const serialized = tx.serialize({ requireAllSignatures: false });
            const instruction = tx.instructions[0];
            const dataHex = instruction?.data
              ? instruction.data instanceof Buffer
                ? instruction.data.toString('hex')
                : Buffer.from(instruction.data).toString('hex')
              : '';

            console.log('[createCampaign] Transaction details:', {
              size: serialized.length,
              instructions: tx.instructions.length,
              programId: instruction?.programId?.toString(),
              dataLength: instruction?.data?.length || 0,
              dataHex:
                dataHex.substring(0, 100) + (dataHex.length > 100 ? '...' : ''),
              accountsCount: instruction?.keys?.length || 0,
              accounts:
                instruction?.keys?.map((k, i) => ({
                  index: i,
                  pubkey: k.pubkey.toString(),
                  isSigner: k.isSigner,
                  isWritable: k.isWritable,
                })) || [],
              note: 'group_state PDA derived from buyer + slot (pattern: [b"group", buyer, slot]) - no group_id needed',
            });
          } catch (serializeError) {
            console.warn(
              '[createCampaign] Could not serialize transaction for logging:',
              serializeError,
            );
          }
        }

        // Simulate transaction to catch errors before sending
        // Note: Simulation requires signers, but we don't want to sign before user approval
        // So we skip simulation if it fails due to missing signers
        try {
          devLog('[createCampaign] Attempting to simulate transaction...');
          // Note: simulateTransaction for legacy Transaction requires signers
          // Since we don't have signers yet (wallet will sign), we'll skip simulation
          // and let the transaction proceed to wallet for user approval
          const simulation = await this.connection.simulateTransaction(
            tx,
            [],
            false,
          );

          if (simulation.value.err) {
            const simError = simulation.value.err;
            console.error('[createCampaign] Simulation failed:', {
              error: simError,
              logs: simulation.value.logs || [],
            });

            // Try to extract error details
            let errorMsg = 'Transaction simulation failed';
            if (simError && typeof simError === 'object') {
              const errorKeys = Object.keys(simError);
              if (errorKeys.length > 0) {
                const errorType = errorKeys[0];
                const errorDetails = (simError as any)[errorType];
                errorMsg = `Transaction simulation failed: ${errorType}${
                  errorDetails ? ` - ${JSON.stringify(errorDetails)}` : ''
                }`;
              }
            }

            // Check logs for more details
            if (simulation.value.logs && simulation.value.logs.length > 0) {
              const errorLogs = simulation.value.logs.filter(
                (log: string) =>
                  log.includes('Error') ||
                  log.includes('failed') ||
                  log.includes('Invalid') ||
                  log.includes('Constraint'),
              );
              if (errorLogs.length > 0) {
                errorMsg += `. Logs: ${errorLogs.join('; ')}`;
              }
            }

            throw new Error(errorMsg);
          }

          devLog('[createCampaign] Simulation successful:', {
            unitsConsumed: simulation.value.unitsConsumed,
            logs: simulation.value.logs?.slice(0, 5) || [], // First 5 logs
          });
        } catch (simError: any) {
          // Handle "No signers" error gracefully - this is expected for unsigned transactions
          if (
            simError?.message?.includes('No signers') ||
            simError?.message?.includes('signer')
          ) {
            devLog(
              '[createCampaign] Simulation skipped (requires signers, will proceed to wallet approval)',
            );
            // Don't throw - this is expected for unsigned transactions
          } else if (simError?.message?.includes('simulation failed')) {
            // Real simulation failure - log but don't block transaction
            console.warn(
              '[createCampaign] Simulation detected potential issue:',
              simError.message,
            );
            // Don't throw - let transaction proceed to wallet, user can decide
          } else {
            // Other simulation errors - log but continue
            console.warn(
              '[createCampaign] Simulation error (non-blocking):',
              simError?.message || simError,
            );
          }
        }
      } catch (prepareError: any) {
        console.error(
          '[createCampaign] Failed to prepare transaction:',
          prepareError,
        );
        throw new Error(
          `Failed to prepare transaction: ${
            prepareError?.message || prepareError?.toString() || 'Unknown error'
          }`,
        );
      }

      let signature: string;
      try {
        devLog('[createCampaign] Attempting to sign and send transaction...');

        // Try to get more detailed error by using sendTransaction with preflight
        // This gives us better error messages from RPC
        if (typeof wallet.signAndSendTransaction === 'function') {
          devLog('[createCampaign] Using signAndSendTransaction (Phantom API)');
          try {
            // First, try to sign the transaction
            const signedTx = await wallet.signTransaction(tx);
            devLog('[createCampaign] Transaction signed successfully');

            // Try to send with preflight to get detailed error
            try {
              signature = await this.connection.sendRawTransaction(
                signedTx.serialize(),
                {
                  skipPreflight: false,
                  maxRetries: 0,
                  preflightCommitment: 'confirmed',
                },
              );
              devLog(
                '[createCampaign] Transaction sent via sendRawTransaction, signature:',
                signature,
              );
            } catch (sendError: any) {
              // Extract detailed error from simulation
              const errorMessage =
                sendError?.message || sendError?.toString() || 'Unknown error';
              const errorLogs = sendError?.logs || [];

              // Check for specific program errors
              if (errorLogs && errorLogs.length > 0) {
                const programErrorLog = errorLogs.find(
                  (log: string) =>
                    log.includes('Error') ||
                    log.includes('AccountDidNotDeserialize') ||
                    log.includes('Failed to deserialize'),
                );

                if (programErrorLog) {
                  console.error('[createCampaign] Program error detected:', {
                    error: programErrorLog,
                    allLogs: errorLogs,
                  });

                  // Check for AccountDidNotDeserialize error
                  if (
                    programErrorLog.includes('AccountDidNotDeserialize') ||
                    programErrorLog.includes('Failed to deserialize')
                  ) {
                    const accountMatch =
                      programErrorLog.match(/account:\s*(\w+)/);
                    const accountName = accountMatch
                      ? accountMatch[1]
                      : 'unknown';
                    throw new Error(
                      `Account "${accountName}" cannot be deserialized. ` +
                        `This usually means the account does not exist or has invalid data. ` +
                        `Please ensure the account is initialized correctly. ` +
                        `Error: ${programErrorLog}`,
                    );
                  }
                }
              }

              // If sendRawTransaction fails, try signAndSendTransaction as fallback
              console.warn(
                '[createCampaign] sendRawTransaction failed, trying signAndSendTransaction:',
                errorMessage,
              );
              try {
                const res = await wallet.signAndSendTransaction(tx);
                signature = res.signature;
                devLog(
                  '[createCampaign] Transaction sent via signAndSendTransaction, signature:',
                  signature,
                );
              } catch (fallbackSendError: any) {
                // If both fail, throw with detailed error
                throw new Error(
                  `Transaction failed: ${errorMessage}. ` +
                    `Program logs: ${errorLogs.slice(0, 5).join('; ')}`,
                );
              }
            }
          } catch (signError: any) {
            // If signTransaction fails, try signAndSendTransaction directly
            try {
              const res = await wallet.signAndSendTransaction(tx);
              signature = res.signature;
              devLog(
                '[createCampaign] Transaction sent via signAndSendTransaction (fallback), signature:',
                signature,
              );
            } catch (fallbackError: any) {
              // Phantom wallet specific error handling
              const phantomError =
                fallbackError?.error ||
                fallbackError ||
                signError?.error ||
                signError;
              const errorCode =
                phantomError?.code || fallbackError?.code || signError?.code;
              const errorMessage =
                phantomError?.message ||
                fallbackError?.message ||
                signError?.message ||
                signError?.toString() ||
                'Unknown error';

              console.error('[createCampaign] Transaction error:', {
                signError: signError?.message || signError?.toString(),
                fallbackError:
                  fallbackError?.message || fallbackError?.toString(),
                error: errorMessage,
                code: errorCode,
                transactionSize: tx.serialize({ requireAllSignatures: false })
                  .length,
                instructionCount: tx.instructions.length,
                fullError: fallbackError || signError,
              });

              // Map common Phantom error codes to user-friendly messages
              let detailedError = errorMessage;
              if (errorCode === 4001) {
                detailedError = 'User rejected the transaction';
              } else if (errorCode === -32603) {
                // Internal error - provide more context
                detailedError =
                  'Transaction validation failed. Possible causes:\n' +
                  '- Missing or invalid accounts\n' +
                  '- Insufficient SOL balance (need SOL for fees + ticket price)\n' +
                  '- Invalid instruction data\n' +
                  '- Account creation failed\n\n' +
                  `Error details: ${errorMessage}`;
              } else if (errorMessage.includes('User rejected')) {
                detailedError = 'User rejected the transaction';
              } else if (
                errorMessage.includes('insufficient funds') ||
                errorMessage.includes('Insufficient')
              ) {
                detailedError =
                  'Insufficient SOL balance. Please ensure you have enough SOL for transaction fees and ticket purchase.';
              }

              throw new Error(detailedError);
            }
          }
        } else {
          devLog('[createCampaign] Using sendTransaction (standard API)');
          signature = await wallet.sendTransaction(tx, this.connection);
          devLog('[createCampaign] Transaction sent, signature:', signature);
        }

        devLog('[createCampaign] Waiting for confirmation...');
        await this.connection.confirmTransaction(signature, 'confirmed');
        try {
          await this.connection.confirmTransaction(signature, 'finalized');
        } catch (finalizedError) {
          console.warn(
            '[createCampaign] confirmTransaction(finalized) failed (continuing with confirmed):',
            finalizedError instanceof Error
              ? finalizedError.message
              : finalizedError,
          );
        }
        const slot = await this.connection.getSlot();
        devLog('Solana buy_tickets_auto tx successful:', signature);

        // Read group_id from the created group_state account
        // Use blockhash_hash that was used in the transaction (saved earlier)
        // IMPORTANT: Account may need a moment to be fully updated after transaction
        // Retry a few times with delay to ensure account is ready
        const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
          wallet.publicKey,
          blockhashHashString,
        );

        // Sử dụng client để lấy group state bằng buyer + blockhash_hash
        const fetchGroupStateWithRetry = async (
          contextLabel: string,
          maxRetries = 5,
          retryDelay = 600,
        ): Promise<any> => {
          let state: any = null;
          let lastError: unknown;

          for (let attempt = 0; attempt < maxRetries; attempt++) {
            if (attempt > 0) {
              devLog(
                `[createCampaign] [${contextLabel}] Retry ${attempt}/${
                  maxRetries - 1
                } - waiting ${retryDelay}ms before retry...`,
              );
              await new Promise(resolve => setTimeout(resolve, retryDelay));
            }

            try {
              state =
                await solanaProgramClient.getGroupStateByBuyerAndBlockhash(
                  wallet.publicKey,
                  blockhashHashString,
                );

              if (state) {
                devLog(
                  '[createCampaign] Group state fetched via buyer+blockhash:',
                  {
                    context: contextLabel,
                    attempt: attempt + 1,
                    pda: state._pda?.toString?.() ?? groupStatePDA.toString(),
                    hasRaw: Boolean(state._raw),
                  },
                );
                return state;
              }
            } catch (attemptError) {
              lastError = attemptError;
              const message =
                attemptError instanceof Error
                  ? attemptError.message
                  : String(attemptError);
              devLog(
                `[createCampaign] [${contextLabel}] Attempt ${
                  attempt + 1
                }/${maxRetries} failed to fetch group state:`,
                message,
              );
            }
          }

          const errorMessage =
            lastError instanceof Error ? lastError.message : String(lastError);
          throw new Error(
            `Failed to fetch group state by buyer/blockhash after ${maxRetries} attempts (${contextLabel}). ` +
              `Last error: ${errorMessage}`,
          );
        };

        let groupStateAccount = await fetchGroupStateWithRetry('initial');
        const getLocalPrizeAmountString = (state: any): string | undefined => {
          const localPrizeAmountValue =
            state?.localPrizeAmount ?? state?.local_prize_amount;
          return localPrizeAmountValue?.toString?.();
        };

        const ensureLocalPrizeAmountReady = async () => {
          if (!extraTokenMint || extraTokenAmountRaw === 0n) {
            return;
          }
          const currentLocalPrize =
            getLocalPrizeAmountString(groupStateAccount);
          if (currentLocalPrize && BigInt(currentLocalPrize) > 0n) {
            return;
          }

          const extraRetries = 3;
          const waitMs = 800;
          for (let attempt = 0; attempt < extraRetries; attempt++) {
            devLog(
              `[createCampaign] localPrizeAmount chưa cập nhật (attempt ${
                attempt + 1
              }/${extraRetries}). Đợi finalized & fetch lại...`,
            );
            await new Promise(resolve => setTimeout(resolve, waitMs));
            try {
              await this.connection.confirmTransaction(signature, 'finalized');
            } catch (waitError) {
              console.warn(
                '[createCampaign] confirmTransaction(finalized) khi chờ localPrizeAmount thất bại:',
                waitError instanceof Error ? waitError.message : waitError,
              );
            }
            groupStateAccount = await fetchGroupStateWithRetry(
              `finalized-refresh-${attempt + 1}`,
            );
            const refreshed = getLocalPrizeAmountString(groupStateAccount);
            if (refreshed && BigInt(refreshed) > 0n) {
              devLog(
                '[createCampaign] localPrizeAmount đã sẵn sàng:',
                refreshed,
              );
              return;
            }
          }
          devLog(
            '[createCampaign] localPrizeAmount vẫn chưa cập nhật sau các lần retry - tiếp tục nhưng sẽ skip deposit nếu vẫn = 0',
          );
        };

        await ensureLocalPrizeAmountReady();

        const rawGroupId =
          groupStateAccount.groupId ??
          groupStateAccount.group_id ??
          groupStateAccount.groupIdString ??
          groupStateAccount.group_id_string;

        if (rawGroupId === undefined || rawGroupId === null) {
          throw new Error('Could not find group_id in fetched group state');
        }

        const groupIdString =
          typeof rawGroupId === 'string'
            ? rawGroupId
            : rawGroupId?.toString?.();

        if (!groupIdString) {
          throw new Error(`Invalid group_id from group state: ${rawGroupId}`);
        }

        const groupIdBigInt = BigInt(groupIdString);
        if (groupIdBigInt === BigInt(0)) {
          throw new Error(
            `Invalid group_id from group state: ${groupIdString}`,
          );
        }

        const groupIdNumber =
          groupIdBigInt <= BigInt(Number.MAX_SAFE_INTEGER)
            ? Number(groupIdBigInt)
            : null;

        devLog('[createCampaign] Successfully read group_id:', {
          groupId: groupIdString,
          pda: groupStateAccount._pda?.toString?.() ?? groupStatePDA.toString(),
        });

        if (
          extraTokenMint &&
          extraTokenAmountRaw > 0n &&
          transferExtraIx &&
          derivedBuyerTokenAta &&
          derivedGroupVaultAta &&
          derivedVaultAuthority &&
          groupStateAccount
        ) {
          try {
            const localPrizeAmount =
              groupStateAccount.localPrizeAmount ??
              groupStateAccount.local_prize_amount;
            const localPrizeAmountString = localPrizeAmount?.toString?.();

            if (
              localPrizeAmountString &&
              BigInt(localPrizeAmountString) > BigInt(0)
            ) {
              devLog(
                '[createCampaign] Preparing deposit_group_spl instruction...',
                {
                  groupId: groupIdString,
                  amount: localPrizeAmountString,
                },
              );

              const depositIx =
                await solanaProgramClient.createDepositGroupSplInstruction(
                  wallet,
                  {
                    groupId: groupIdBigInt,
                    blockhashHash: blockhashHashString,
                    buyer: wallet.publicKey,
                    extraTokenMint: new PublicKey(extraTokenMint),
                    amount: localPrizeAmountString,
                    buyerTokenAta: derivedBuyerTokenAta,
                    vaultAuthority: derivedVaultAuthority,
                    tokenVaultAta: derivedGroupVaultAta,
                  },
                );

              const depositTx = new Transaction().add(depositIx);
              await this.prepareTransaction(depositTx);

              postCreationTransactions.push({
                label: 'deposit_group_spl',
                transaction: depositTx,
                context: {
                  groupId: groupIdString,
                  amount: localPrizeAmountString,
                },
              });
            } else {
              devLog(
                '[createCampaign] Skipping deposit_group_spl - localPrizeAmount is zero or missing',
              );
            }
          } catch (depositError: any) {
            let depositLogs: string[] | undefined;
            if (depositError instanceof SendTransactionError) {
              try {
                depositLogs = await depositError.getLogs(this.connection);
              } catch (logError) {
                console.warn(
                  '[createCampaign] Could not fetch logs from SendTransactionError:',
                  logError instanceof Error ? logError.message : logError,
                );
              }
            } else if (typeof depositError?.getLogs === 'function') {
              try {
                depositLogs = await depositError.getLogs(this.connection);
              } catch (logError) {
                console.warn(
                  '[createCampaign] Custom error getLogs call failed:',
                  logError instanceof Error ? logError.message : logError,
                );
              }
            }

            console.error(
              '[createCampaign] Failed to deposit group SPL tokens:',
              {
                error: depositError,
                logs: depositLogs,
              },
            );

            const baseMessage =
              depositError?.message ||
              depositError?.toString?.() ||
              'Unknown deposit_group_spl error';
            const logsMessage = depositLogs?.length
              ? ` Program logs: ${depositLogs.join(' | ')}`
              : '';
            throw new Error(
              `[deposit_group_spl] ${baseMessage}.${logsMessage}`,
            );
          }
        }

        // Mint NFTs for each ticket after buy_tickets_auto succeeds
        // This is required for claim jackpot flows (winner must own NFT)
        try {
          const startTicketId =
            groupStateAccount.startTicketId?.toNumber?.() ||
            groupStateAccount.start_ticket_id?.toNumber?.() ||
            0;

          if (startTicketId > 0) {
            devLog('[createCampaign] Minting NFTs for tickets:', {
              startTicketId,
              numTickets: params.numMint,
            });

            const globalStateForMint =
              await solanaProgramClient.getGlobalState();
            if (!globalStateForMint) {
              throw new Error('Global state not found');
            }

            const adminPk =
              globalStateForMint.admin instanceof PublicKey
                ? globalStateForMint.admin
                : new PublicKey(globalStateForMint.admin.toString());

            if (wallet.publicKey.equals(adminPk)) {
              for (let i = 0; i < params.numMint; i++) {
                const ticketId = startTicketId + i;
                const receiverPk =
                  receivers.length > i ? receivers[i] : wallet.publicKey;

                try {
                  const { transaction } = await this.buildMintNftTransaction(
                    {
                      ticketId: ticketId.toString(),
                      receiver: receiverPk.toString(),
                      group: {
                        blockhashHash: blockhashHashString,
                        buyer: wallet.publicKey.toString(),
                      },
                    },
                    wallet,
                  );

                  postCreationTransactions.push({
                    label: 'mint_nft',
                    transaction,
                    context: {
                      ticketId,
                      receiver: receiverPk.toString(),
                    },
                  });
                } catch (mintError: any) {
                  console.warn(
                    `[createCampaign] Error preparing mint NFT tx for ticket ${ticketId}:`,
                    mintError?.message || mintError,
                  );
                }
              }
            } else {
              devLog(
                '[createCampaign] Skipping NFT minting - wallet is not admin. NFTs can be minted later by admin.',
              );
            }
          }
        } catch (mintNftError: any) {
          console.warn(
            '[createCampaign] Failed to mint NFTs (non-blocking):',
            mintNftError?.message || mintNftError,
          );
        }

        if (postCreationTransactions.length > 0) {
          const transactionsOnly = postCreationTransactions.map(
            item => item.transaction,
          );
          const supportsSignAll =
            typeof (wallet as any).signAllTransactions === 'function';
          let signedTransactions: Transaction[] | null = null;

          if (supportsSignAll) {
            try {
              signedTransactions = await wallet.signAllTransactions(
                transactionsOnly,
              );
              devLog(
                '[createCampaign] Signed post-creation transactions via signAllTransactions',
                {
                  count: transactionsOnly.length,
                },
              );
            } catch (signAllError) {
              console.warn(
                '[createCampaign] signAllTransactions failed, falling back to per-transaction signing:',
                signAllError instanceof Error
                  ? signAllError.message
                  : signAllError,
              );
              signedTransactions = null;
            }
          }

          for (let i = 0; i < postCreationTransactions.length; i++) {
            const { label, transaction, context } =
              postCreationTransactions[i];
            try {
              let postSignature: string;
              if (signedTransactions) {
                postSignature = await this.connection.sendRawTransaction(
                  signedTransactions[i].serialize(),
                  {
                    skipPreflight: false,
                    maxRetries: 0,
                    preflightCommitment: 'confirmed',
                  },
                );
              } else if (typeof wallet.signAndSendTransaction === 'function') {
                const res = await wallet.signAndSendTransaction(transaction);
                postSignature = res.signature;
              } else if (typeof wallet.signTransaction === 'function') {
                const signedTx = await wallet.signTransaction(transaction);
                postSignature = await this.connection.sendRawTransaction(
                  signedTx.serialize(),
                  {
                    skipPreflight: false,
                    maxRetries: 0,
                    preflightCommitment: 'confirmed',
                  },
                );
              } else {
                postSignature = await wallet.sendTransaction(
                  transaction,
                  this.connection,
                );
              }

              await this.connection.confirmTransaction(
                postSignature,
                'confirmed',
              );
              devLog(
                `[createCampaign] ${label} tx success:`,
                postSignature,
                context,
              );
            } catch (postTxError: any) {
              console.error(
                `[createCampaign] ${label} tx failed:`,
                postTxError?.message || postTxError,
                context,
              );
              throw new Error(
                `[${label}] ${
                  postTxError?.message ||
                  postTxError?.toString?.() ||
                  'Unknown error'
                }`,
              );
            }
          }
        }

        return {
          success: true,
          signature,
          slot,
          blockhashHash: blockhashHashString, // Return blockhashHash as string for campaign storage (Solana buy_tickets_auto)
          creatorWalletAddress: wallet.publicKey.toString(), // Return creator wallet address (buyer)
        };
      } catch (txError: any) {
        // Enhanced error logging for transaction failures
        const errorMessage =
          txError?.message ||
          txError?.toString() ||
          'Unknown transaction error';
        const errorLogs = txError?.logs || [];
        const errorCode = txError?.code;

        console.error('[createCampaign] Transaction failed:', {
          error: errorMessage,
          code: errorCode,
          logs: errorLogs,
          instruction: 'buy_tickets_auto',
          params: {
            numTickets: params.numMint,
            extraToken: params.extraToken,
            extraTokenAmount: params.extraTokenAmount,
            receiversCount: params.receivers?.length || 0,
          },
          fullError: txError,
        });

        // Try to extract more specific error information
        let detailedError = errorMessage;

        // Check for program error logs
        if (errorLogs && errorLogs.length > 0) {
          const programErrorLog = errorLogs.find(
            (log: string) =>
              log.includes('Error') ||
              log.includes('failed') ||
              log.includes('Invalid'),
          );
          if (programErrorLog) {
            detailedError = `${errorMessage}. Program log: ${programErrorLog}`;
          }
        }

        // Check for simulation errors if available
        if (txError?.err) {
          const simError = txError.err;
          if (typeof simError === 'object') {
            const errorKeys = Object.keys(simError);
            if (errorKeys.length > 0) {
              detailedError = `${errorMessage}. Program error: ${errorKeys[0]}`;
            }
          }
        }

        return {
          success: false,
          error: detailedError,
          logs: errorLogs,
          code: errorCode,
        };
      }
    } catch (error) {
      console.error('Failed to create campaign:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      const errorStack = error instanceof Error ? error.stack : undefined;

      return {
        success: false,
        error: errorMessage,
        stack: errorStack,
      };
    }
  }
  async checkCampaignTimeStatus(_lixiBagAddress: string): Promise<any> {
    try {
      // Use getGlobalState() which already decodes closing_time correctly
      // This avoids duplicate decode logic and ensures consistency
      const globalState = await solanaProgramClient.getGlobalState();

      if (!globalState) {
        throw new Error('Failed to fetch global state');
      }

      // Process global state to extract and format time information
      // processGlobalState handles BN objects, milliseconds conversion, etc.
      return this.processGlobalState(globalState);
    } catch (error) {
      console.error('Error in checkCampaignTimeStatus:', error);
      throw new Error(
        `Failed to get campaign time status: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  private processGlobalState(globalState: any): any {
    // Handle BN object or BigNumber if present
    let rawClosingTime =
      globalState.closingTime || globalState.closing_time || 0;
    if (
      rawClosingTime &&
      typeof rawClosingTime === 'object' &&
      'toNumber' in rawClosingTime
    ) {
      rawClosingTime = rawClosingTime.toNumber();
    } else if (
      rawClosingTime &&
      typeof rawClosingTime === 'object' &&
      'toString' in rawClosingTime
    ) {
      rawClosingTime = Number(rawClosingTime.toString());
    }

    const closingTimeRaw = Number(rawClosingTime);

    // Validate closingTimeRaw is a valid number
    if (!Number.isFinite(closingTimeRaw) || isNaN(closingTimeRaw)) {
      throw new Error(
        `Invalid closingTime value in global state: ${rawClosingTime}. ` +
          `This indicates the global state account may be corrupted or not properly initialized. ` +
          `Please ensure the global state is initialized correctly.`,
      );
    }

    // Blockchain stores closing_time as i64 Unix timestamp in SECONDS (not milliseconds)
    // Check if it's likely in milliseconds format (> timestamp for year 2100 in seconds)
    // Year 2100 timestamp in seconds: 4102444800
    let closingTimeSeconds: number;
    if (closingTimeRaw > 4102444800) {
      // Likely in milliseconds, convert to seconds
      closingTimeSeconds = Math.floor(closingTimeRaw / 1000);
    } else if (closingTimeRaw <= 0) {
      // Invalid or unset closing time - return 0 to indicate no valid time
      closingTimeSeconds = 0;
    } else {
      // Already in seconds
      closingTimeSeconds = closingTimeRaw;
    }

    const currentTime = Math.floor(Date.now() / 1000);

    // If closingTime is 0 or invalid, timeRemaining should be 0
    // Don't calculate timeRemaining if closingTime is not set
    const timeRemaining =
      closingTimeSeconds > 0
        ? Math.max(0, closingTimeSeconds - currentTime)
        : 0;
    const isExpired =
      closingTimeSeconds > 0 && currentTime > closingTimeSeconds;

    // Create Date objects only if closingTimeSeconds is valid
    // Validate that the timestamp is reasonable (not too far in past/future)
    const MIN_VALID_TIMESTAMP = 946684800; // Year 2000
    const MAX_VALID_TIMESTAMP = 4102444800; // Year 2100
    const isValidTimestamp =
      closingTimeSeconds >= MIN_VALID_TIMESTAMP &&
      closingTimeSeconds <= MAX_VALID_TIMESTAMP;

    // Always return a valid Date object (interface requires Date, not Date | null)
    let closingTimeDate: Date;
    if (closingTimeSeconds > 0 && isValidTimestamp) {
      try {
        const date = new Date(closingTimeSeconds * 1000);
        // Double-check the Date is valid
        if (isNaN(date.getTime())) {
          throw new Error(
            `Failed to create valid Date from closingTimeSeconds: ${closingTimeSeconds}. ` +
              `This indicates invalid timestamp data in global state.`,
          );
        }
        closingTimeDate = date;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        throw new Error(
          `Error creating Date from closingTimeSeconds: ${closingTimeSeconds}. ` +
            `Error: ${errorMessage}. ` +
            `This indicates invalid timestamp data in global state.`,
        );
      }
    } else if (closingTimeSeconds === 0) {
      // Closing time is 0 (not set) - return a Date object but log warning
      // This is valid state (campaign not started yet)
      closingTimeDate = new Date(0); // Epoch time
    } else {
      // Invalid timestamp (outside valid range)
      throw new Error(
        `Invalid closing time timestamp: ${closingTimeSeconds}. ` +
          `Valid range: ${MIN_VALID_TIMESTAMP} to ${MAX_VALID_TIMESTAMP} (Year 2000 to 2100). ` +
          `This indicates corrupted data in global state.`,
      );
    }

    return {
      currentTime,
      closingTime: closingTimeSeconds,
      timeRemaining,
      isExpired,
      closingTimeDate, // Always a valid Date object
      currentTimeDate: new Date(currentTime * 1000),
    };
  }

  private normalizeNumber(value: any, label: string): number {
    if (value === null || value === undefined) {
      throw new Error(`${label} is required`);
    }
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) {
        throw new Error(`${label} is not a finite number`);
      }
      return value;
    }
    if (typeof value === 'bigint') {
      return Number(value);
    }
    if (typeof value === 'string') {
      const parsed = Number(value);
      if (Number.isNaN(parsed)) {
        throw new Error(`${label} string cannot be parsed to number`);
      }
      return parsed;
    }
    if (typeof value === 'object') {
      if (typeof value.toNumber === 'function') {
        return value.toNumber();
      }
      if (typeof value.toString === 'function') {
        const parsed = Number(value.toString());
        if (!Number.isNaN(parsed)) {
          return parsed;
        }
      }
    }
    throw new Error(`Unsupported ${label} format`);
  }

  private toHashBuffer(value: any, label: string): Buffer {
    if (!value) {
      throw new Error(`${label} is required`);
    }
    if (Buffer.isBuffer(value)) {
      return Buffer.from(value);
    }
    if (value instanceof Uint8Array) {
      return Buffer.from(value);
    }
    if (Array.isArray(value)) {
      return Buffer.from(value);
    }
    if (typeof value === 'string') {
      const normalized = value.startsWith('0x') ? value.slice(2) : value;
      return Buffer.from(normalized, 'hex');
    }
    if (value?.data && Array.isArray(value.data)) {
      return Buffer.from(value.data);
    }
    throw new Error(`Unsupported ${label} format`);
  }

  private calculateLocalWinningTicket(
    globalState: any,
    groupState: any,
  ): number {
    const winningHashBuffer = this.toHashBuffer(
      globalState?.winningHash ?? globalState?.winning_hash,
      'global winning hash',
    );
    const groupHashBuffer = this.toHashBuffer(
      groupState?.hash ?? groupState?.groupHash ?? groupState?.group_hash,
      'group hash',
    );

    if (winningHashBuffer.length < 16 || groupHashBuffer.length < 16) {
      throw new Error(
        'Insufficient hash data to calculate local winning ticket',
      );
    }

    const xorBuffer = Buffer.alloc(16);
    for (let i = 0; i < 16; i++) {
      xorBuffer[i] = winningHashBuffer[i] ^ groupHashBuffer[i];
    }

    const groupSize = this.normalizeNumber(
      groupState?.size ?? groupState?.groupSize,
      'group size',
    );
    if (groupSize <= 0) {
      throw new Error('Group size must be greater than 0');
    }

    const startTicketId = this.normalizeNumber(
      groupState?.startTicketId ??
        groupState?.start_ticket_id ??
        groupState?.start_ticketId,
      'group start ticket id',
    );

    const localIndexBigInt = xorBuffer.readBigUInt64LE(0) % BigInt(groupSize);
    const localIndex = Number(localIndexBigInt);

    return startTicketId + localIndex;
  }

  // Connection method
  getConnection(): Connection {
    return this.connection;
  }

  // Global state method
  async getGlobalState(): Promise<any> {
    // No fallback - throw error if failed
    // This ensures issues are caught early instead of returning null
    return await solanaProgramClient.getGlobalState();
  }

  // Group state method
  async getGroupState(groupId: number | string): Promise<any> {
    const { parseGroupIdFromString } = await import('../solana/pda');
    const parsedGroupId =
      typeof groupId === 'string' ? parseGroupIdFromString(groupId) : groupId;

    // No fallback - throw error if failed
    // This ensures issues are caught early instead of returning null
    return await solanaProgramClient.getGroupState(parsedGroupId);
  }

  // Create buy tickets instruction method
  async createBuyTicketsInstruction(params: {
    group: GroupIdentifier;
    quantity: number;
    price: string;
    extraTokenMint?: string;
    extraTokenAmount?: number;
    receivers?: string[];
  }): Promise<any> {
    try {
      const wallet = this.ensureWallet();
      if (!wallet) {
        throw new Error('Wallet not connected');
      }

      const groupParams = this.ensureAutoGroupIdentifier(params.group, {
        requireGroupId: true,
      });

      const extraTokenMint = params.extraTokenMint
        ? new PublicKey(params.extraTokenMint)
        : undefined;
      const receivers = (params.receivers || []).map(
        addr => new PublicKey(addr),
      );

      // Parse string groupId to number or bigint first
      const { parseGroupIdFromString, groupIdToBN, groupIdToNumber } =
        await import('../solana/pda');
      const parsed = parseGroupIdFromString(groupParams.groupId as string);
      const groupIdBN = groupIdToBN(parsed);
      // createBuyTicketsInstruction expects number, but we'll pass BN and let it handle conversion
      // For now, try to convert to number (may lose precision for very large numbers)
      let groupIdNum: number;
      try {
        groupIdNum = groupIdToNumber(parsed);
      } catch (error: any) {
        // If too large, use the BN value converted to number (may lose precision)
        // This is a temporary solution - createBuyTicketsInstruction should be updated to accept BN
        groupIdNum = Number(groupIdBN.toString());
        console.warn(
          '[createBuyTicketsInstruction] group_id exceeds safe integer range:',
          groupParams.groupId,
        );
      }

      return await solanaProgramClient.createBuyTicketsInstruction(
        wallet,
        {
          groupId: groupIdNum,
          numTickets: params.quantity,
          extraTokenMint,
          extraTokenAmount: params.extraTokenAmount || 0,
          receivers,
        },
        {
          blockhashHash: groupParams.blockhashHash,
          buyer: groupParams.buyerPublicKey,
        },
      );
    } catch (error) {
      console.error('Failed to create buy tickets instruction:', error);
      return null;
    }
  }

  // Generic event subscription (Solana)
  subscribeToEvent(
    eventName: string,
    callback: (data: any) => void,
    options?: { address?: string; filterArgs?: any[] },
  ): () => void {
    try {
      // Use program ID from options or default program ID
      // Validate that the address is a valid Solana address (not EVM 0x... format)
      let programId: PublicKey;

      if (options?.address) {
        const address = options.address.trim();
        // Skip if it's an EVM address (starts with 0x)
        if (address.startsWith('0x') || address.length === 0) {
          devLog(
            `Invalid Solana address provided (${address}), using default program ID`,
          );
          programId = new PublicKey(SOLANA_PROGRAM_ID);
        } else {
          try {
            programId = new PublicKey(address);
          } catch (error) {
            devLog(
              `Failed to parse address ${address} as PublicKey, using default program ID:`,
              error,
            );
            programId = new PublicKey(SOLANA_PROGRAM_ID);
          }
        }
      } else {
        programId = new PublicKey(SOLANA_PROGRAM_ID);
      }

      devLog(
        `Subscribing to ${eventName} events for program: ${programId.toString()}`,
      );

      // Subscribe to logs from the program
      const subscriptionId = this.connection.onLogs(
        programId,
        async (logs, context) => {
          try {
            // Check if any log contains the event name
            const matchingLog = logs.logs.find(
              log => log.includes(eventName) || log.includes('MintLixi'),
            );

            if (matchingLog) {
              devLog(`Found ${eventName} event in logs:`, logs);

              // Fetch the full transaction to decode event data properly
              let eventData: Record<string, any> = {};
              try {
                const transaction = await this.connection.getTransaction(
                  logs.signature,
                  {
                    commitment: 'confirmed',
                    maxSupportedTransactionVersion: 0,
                  },
                );

                if (transaction) {
                  // Try to decode Anchor event using EventCoder first
                  try {
                    const { BorshEventCoder } = await import(
                      '@coral-xyz/anchor'
                    );
                    const idl = await import('../../idl/lixinft_solana.json');

                    if (idl && idl.default && transaction.meta?.logMessages) {
                      const eventCoder = new BorshEventCoder(
                        idl.default as any,
                      );
                      // Decode events from log messages
                      const logMessages = transaction.meta.logMessages;
                      const events = eventCoder.decode(logMessages.join('\n'));

                      // Find MintLixi event
                      if (events && Array.isArray(events)) {
                        const mintLixiEvent = events.find(
                          (e: any) =>
                            e.name === 'MintLixi' || e.name === 'mintLixi',
                        );

                        if (mintLixiEvent && mintLixiEvent.data) {
                          devLog(
                            'Decoded MintLixi event from Anchor:',
                            mintLixiEvent.data,
                          );
                          eventData = {
                            owner: mintLixiEvent.data.owner?.toString(),
                            groupId:
                              mintLixiEvent.data.groupId?.toString() ||
                              mintLixiEvent.data.group_id?.toString(),
                            numTickets:
                              mintLixiEvent.data.numTickets ||
                              mintLixiEvent.data.num_tickets,
                            startTicketId:
                              mintLixiEvent.data.startTicketId?.toString() ||
                              mintLixiEvent.data.start_ticket_id?.toString(),
                            purchaseDate:
                              mintLixiEvent.data.purchaseDate ||
                              mintLixiEvent.data.purchase_date,
                          };
                        }
                      } else if (
                        events &&
                        typeof events === 'object' &&
                        'name' in events
                      ) {
                        // Single event object
                        if (
                          events.name === 'MintLixi' ||
                          events.name === 'mintLixi'
                        ) {
                          devLog(
                            'Decoded MintLixi event from Anchor:',
                            events.data,
                          );
                          eventData = {
                            owner: events.data?.owner?.toString(),
                            groupId:
                              events.data?.groupId?.toString() ||
                              events.data?.group_id?.toString(),
                            numTickets:
                              events.data?.numTickets ||
                              events.data?.num_tickets,
                            startTicketId:
                              events.data?.startTicketId?.toString() ||
                              events.data?.start_ticket_id?.toString(),
                            purchaseDate:
                              events.data?.purchaseDate ||
                              events.data?.purchase_date,
                          };
                        }
                      }
                    }
                  } catch (decodeError) {
                    devLog(
                      'Failed to decode Anchor event, trying manual parsing:',
                      decodeError,
                    );
                    // Continue to manual parsing below
                  }

                  // If Anchor decode didn't find event, try manual parsing
                  if (!eventData.groupId) {
                    try {
                      eventData = this.parseSolanaEventFromTransaction(
                        transaction,
                        eventName,
                      );
                    } catch (parseError) {
                      devLog(
                        'Failed to parse event from transaction:',
                        parseError,
                      );
                      // If manual parsing also fails, throw error instead of using log parsing
                      throw new Error(
                        `Failed to decode ${eventName} event from transaction ${logs.signature}. ` +
                          `Anchor decode failed and manual parsing also failed. ` +
                          `This may indicate the transaction does not contain the expected event or the event format has changed. ` +
                          `Error: ${
                            parseError instanceof Error
                              ? parseError.message
                              : String(parseError)
                          }`,
                      );
                    }
                  }
                } else {
                  // Transaction fetch failed - throw error instead of using log parsing
                  throw new Error(
                    `Failed to fetch transaction ${logs.signature} for event parsing. ` +
                      `This is required to decode ${eventName} event data. ` +
                      `Please check network connection and transaction signature.`,
                  );
                }
              } catch (txError: unknown) {
                const errorMessage =
                  txError instanceof Error ? txError.message : String(txError);
                // If it's our custom error, re-throw it
                if (
                  txError instanceof Error &&
                  (txError.message.includes('Failed to decode') ||
                    txError.message.includes('Failed to fetch'))
                ) {
                  throw txError;
                }
                // Otherwise, wrap in error
                throw new Error(
                  `Failed to process ${eventName} event from transaction ${logs.signature}: ${errorMessage}. ` +
                    `This may indicate the transaction format is invalid or the event is missing.`,
                );
              }

              // Call the callback with formatted event data
              // Structure it to match what useMintLixiEvent expects
              // The handler expects the event data properties at the top level for compatibility
              const formattedEvent = {
                eventName,
                chain: this.chain,
                timestamp: Date.now(),
                // For Solana, put parsed event data directly accessible
                // Matching the structure expected by useMintLixiEvent handler
                ...eventData,
                data: eventData,
                signature: logs.signature,
                slot: context.slot,
              };
              callback(formattedEvent);
            }
          } catch (error) {
            console.error(`Error processing ${eventName} event:`, error);
          }
        },
        'confirmed',
      );

      // Return unsubscribe function
      return () => {
        try {
          this.connection.removeOnLogsListener(subscriptionId);
          devLog(`Unsubscribed from ${eventName} events`);
        } catch (error) {
          console.error(`Error unsubscribing from ${eventName}:`, error);
        }
      };
    } catch (error) {
      console.error(`Failed to subscribe to ${eventName}:`, error);
      return () => void 0;
    }
  }

  /**
   * Parse Solana event from transaction
   * Attempts to decode Anchor events from transaction metadata
   */
  private parseSolanaEventFromTransaction(
    transaction: any,
    eventName: string,
  ): Record<string, any> {
    try {
      const eventData: Record<string, any> = {};

      // Try to extract event data from transaction logs
      if (transaction.meta?.logMessages) {
        const logs = transaction.meta.logMessages;

        // For MintLixi events, look for relevant data in logs or instruction data
        if (eventName === 'MintLixi') {
          // Try to extract owner from transaction
          if (transaction.transaction?.message?.accountKeys) {
            // The first writable signer is typically the owner/buyer
            const accountKeys = transaction.transaction.message.accountKeys;
            const signer = accountKeys.find(
              (key: any, index: number) =>
                transaction.transaction.message.header.numRequiredSignatures >
                index,
            );
            if (signer) {
              eventData.owner = signer.pubkey?.toString() || signer.toString();
            }
          }

          // Try to parse structured logs if available
          // Anchor programs often emit events in logs with data
          const mintLog = logs.find(
            (log: string) =>
              log.toLowerCase().includes('mintlixi') ||
              log.toLowerCase().includes('mint_lixi'),
          );

          if (mintLog) {
            // Try to extract additional data from logs
            // Common patterns in Anchor logs
            const groupIdMatch = mintLog.match(/group[_\s]*id[=:]\s*(\d+)/i);
            if (groupIdMatch) {
              eventData.groupId = groupIdMatch[1];
            }

            const numTicketsMatch = mintLog.match(
              /num[_\s]*tickets?[=:]\s*(\d+)/i,
            );
            if (numTicketsMatch) {
              eventData.numTickets = parseInt(numTicketsMatch[1], 10);
            }

            const ticketIdMatch = mintLog.match(
              /(?:start[_\s]*)?ticket[_\s]*id[=:]\s*(\d+)/i,
            );
            if (ticketIdMatch) {
              eventData.startTicketId = ticketIdMatch[1];
            }
          }

          // Extract groupId from instruction data if available
          if (transaction.transaction?.message?.instructions) {
            for (const ix of transaction.transaction.message.instructions) {
              if (ix.programId?.toString() === SOLANA_PROGRAM_ID) {
                // Try to decode instruction data to extract parameters
                // This would require knowing the instruction format
                // For now, we'll rely on logs and account keys
              }
            }
          }
        }
      }

      return eventData;
    } catch (error) {
      console.error('Error parsing Solana event from transaction:', error);
      return {};
    }
  }

  // Event listeners
  onWalletChange(callback: (wallet: WalletInfo | null) => void): () => void {
    if (!this.listeners.has('walletChanged')) {
      this.listeners.set('walletChanged', []);
    }
    this.listeners.get('walletChanged')!.push(callback);

    return () => {
      const listeners = this.listeners.get('walletChanged');
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    };
  }

  onChainChange(callback: (chain: SupportedChain) => void): () => void {
    if (!this.listeners.has('chainChanged')) {
      this.listeners.set('chainChanged', []);
    }
    this.listeners.get('chainChanged')!.push(callback);

    return () => {
      const listeners = this.listeners.get('chainChanged');
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    };
  }

  private emit(event: string, ...args: any[]): void {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }
}
